import React, { useState, useRef, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { ThemeContext, getTheme } from './theme';
import taleoLogo from './assets/Taleo_Logo_v1.png';
import { HambergerMenu, Book, Home2, Profile, Story, People, Moon, ArrowDown2, ArrowLeft2, Magicpen, Microphone2, Heart, Trash, NoteText, Lock, Brush, Eraser, Backward10Seconds, Forward10Seconds, Play, Pause } from 'iconsax-react';
import { Sun } from 'lucide-react';
import SignupScreen from './screens/SignupScreen';
import LoginScreen from './screens/LoginScreen';
import SelectProfileScreen from './screens/SelectProfileScreen';
import PreferencesScreen from './screens/PreferencesScreen';
import ChildProfileScreen from './screens/ChildProfileScreen';
import StatisticsScreen from './screens/StatisticsScreen';
import ManageProfileScreen from './screens/ManageProfileScreen';
import EditProfileScreen from './screens/EditProfileScreen';
import EditKidProfileScreen from './screens/EditKidProfileScreen';
import NotificationsScreen from './screens/NotificationsScreen';
import SettingsScreen from './screens/SettingsScreen';
import CommunityScreen from './screens/CommunityScreen';
import LibraryScreen from './screens/LibraryScreen';
import ShareThoughtsModal from './components/ShareThoughtsModal';
import StoryPreviewModal from './components/StoryPreviewModal';
import Button from './components/Button';

// Images from Figma
const MASCOT_URL = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1777995760/topbar_image_2_ol4epo.webp';
const AVATAR_URL = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776961469/kiraz_dp_gs0phi.webp';
const EDA_URL   = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776961468/eda_dp_fszpol.webp';
const HAMMY_URL = 'https://www.figma.com/api/mcp/asset/aaa4ce6d-c74d-4dc1-b306-d4032ffd7d0b';

// Illustration images (from Figma, valid ~7 days)
const ILLUS_DISNEY     = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960918/illustration_type_1_pymibl.webp';
const ILLUS_COMIC      = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960921/illustration_type_2_r3beuu.webp';
const ILLUS_PUPPET     = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960920/illustration_type_3_evaj0s.webp';
const ILLUS_WATERCOLOR = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960921/illustration_type_4_kdwejs.webp';
const ILLUS_PENCIL     = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960919/illustration_type_5_usdpnu.webp';
const ILLUS_GHIBLI     = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960920/illustration_type_6_yofvqn.webp';
const STORY_COVER_URL          = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960922/main_story1_image_1_t6nzhp.webp';
const DRAW_TELL_MASCOT_URL  = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1777995788/topbar_image_3_tfeaxd.webp';
const DRAW_TELL_CANVAS_BG_URL = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1777035766/birthday_image_ozm5uv.webp';
const MODE_MASCOT_URL       = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1777995800/topbar_image_1_osbiab.webp';
const COMMUNITY_MASCOT_URL  = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1777995775/topbar_image_5_qgitd1.webp';
const LIBRARY_MASCOT_URL    = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776961473/topbar_image_4_hw1s47.webp';
const DRAW_TELL_COVER_URL      = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960924/main_story3_image_1_thtswr.webp';
const DRAW_TELL_READ_HERO_URL  = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960924/main_story3_image_1_thtswr.webp';
const DRAW_TELL_READ_IMG1_URL  = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960926/main_story3_image_2_qluwuo.webp';
const DRAW_TELL_READ_IMG2_URL  = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960924/main_story3_image_3_vcihc7.webp';
const STORY_READ_HERO_URL = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960922/main_story1_image_1_t6nzhp.webp';
const STORY_READ_MID_URL  = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960927/story_thumbnail_7_tgddod.webp';
const STORY_READ_IMG2_URL = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960925/story_thumbnail_2_dg443d.webp';

// Story Dice card images (3D dice illustrations from Figma)
const DICE_IMG_CHARACTER      = 'https://www.figma.com/api/mcp/asset/bfbce47c-47ff-49ae-b767-5b173413c826';
const DICE_IMG_SIDE_CHARACTER = 'https://www.figma.com/api/mcp/asset/3af4d90c-3f8b-434b-a017-006fd4552b39';
const DICE_IMG_THEME          = 'https://www.figma.com/api/mcp/asset/8dd0da91-335d-47d4-90b2-30c28a743b75';
const DICE_IMG_OBJECT         = 'https://www.figma.com/api/mcp/asset/11e52613-1757-4948-80d5-2bdb82440b51';

// Listen mode audio screen assets (concentric rings player)
const LISTEN_ALBUM_ART_URL       = 'https://www.figma.com/api/mcp/asset/4e83c1c2-b9d0-4367-8788-1eadf830ab25';
const DRAW_TELL_LISTEN_ART_URL   = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960924/main_story3_image_1_thtswr.webp';
const LISTEN_BACKWARD_URL        = 'https://www.figma.com/api/mcp/asset/bf73906d-0de3-4d97-9a0a-56dce48313d9';
const LISTEN_PAUSE_URL           = 'https://www.figma.com/api/mcp/asset/ec2de5a5-8d65-4003-8b53-2a9c0ec14802';
const LISTEN_FORWARD_URL         = 'https://www.figma.com/api/mcp/asset/68f62653-8077-4b8f-b11b-e640b87da061';

// Landing screen
const LANDING_BG_URL = 'https://www.figma.com/api/mcp/asset/a7634e06-ecc9-489b-9542-fd1c6972f75e';

// Watch mode video screen assets (full-bleed player)
const WATCH_BG_URL               = 'https://www.figma.com/api/mcp/asset/4657f6c6-8649-4b7a-afce-b24583cd6f1a';
const DRAW_TELL_WATCH_BG_URL     = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960924/main_story3_image_1_thtswr.webp';
const WATCH_BACKWARD_URL         = 'https://www.figma.com/api/mcp/asset/533c1e53-5a0d-42b9-8d8e-13ef0eb32531';

// Settings screen assets
const SETTINGS_DOGGO_URL         = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1777995818/topbar_image_6_w0t3fg.webp';
const DICE_MASCOT_URL            = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1777995819/topbar_image_7_f5th7b.webp';
const SETTINGS_ICON_NOTIFICATION = 'https://www.figma.com/api/mcp/asset/8d36d8e8-e235-4c5e-90e2-a27f314694af';
const SETTINGS_ICON_LANGUAGE     = 'https://www.figma.com/api/mcp/asset/64a3cde2-212a-405b-ab3c-912f41493999';
const SETTINGS_ICON_NARRATION    = 'https://www.figma.com/api/mcp/asset/878474cf-a4ef-422c-a1bc-fef2995c5a06';
const SETTINGS_ICON_ACCOUNT      = 'https://www.figma.com/api/mcp/asset/37a72eae-9864-47a6-917f-a954f8d4528e';
const SETTINGS_ICON_KIDS_PROFILE = 'https://www.figma.com/api/mcp/asset/81197135-b930-4a81-9517-58b6cd17479d';
const SETTINGS_ICON_PRIVACY      = 'https://www.figma.com/api/mcp/asset/0270d0e4-c7a0-443f-90ee-5e8ea2ef7ee4';
const SETTINGS_ICON_PERMISSIONS  = 'https://www.figma.com/api/mcp/asset/d4cc36fb-6f3d-401b-9e9a-67816be74f8d';
const SETTINGS_ICON_FAQ          = 'https://www.figma.com/api/mcp/asset/642a1919-2057-40c2-b4e2-2a8d827adcc1';
const SETTINGS_ICON_FEEDBACK     = 'https://www.figma.com/api/mcp/asset/a8e55bb6-89b0-4a75-b422-7fadbda0a0db';
const SETTINGS_ICON_TERMS        = 'https://www.figma.com/api/mcp/asset/52840124-1742-4ec5-aadd-0ec2d2f1a415';
const WATCH_PAUSE_URL       = 'https://www.figma.com/api/mcp/asset/b47180b7-f8c1-42dd-8f98-d779b75e4ff7';
const WATCH_FORWARD_URL     = 'https://www.figma.com/api/mcp/asset/6df229fe-7c79-483f-bbcf-d2fa16c1d246';

const STORY_PREVIEW_TEXT = `Ryan, a curious 6-year-old boy, loved exploring the forest behind his house. He wore a wide-brimmed hat, carried a small backpack, and proudly called himself the Forest Explorer. Every morning after breakfast, he grabbed his journal and walked into the woods with a big smile on his face.

One sunny day, Ryan discovered a new path filled with tall trees, colorful leaves, and birdsong in the air. As he followed the trail, he heard a soft hopping sound behind him. It was Rabbit, his cheerful friend with floppy ears and a twitchy nose.

"Ready for a new adventure?" Rabbit asked.
"Always," Ryan said with a grin.

They wandered deeper into the forest. Along the way, Ryan spotted something near a tree stump. It was a perfectly round, clean football. He picked it up and bounced it on the ground.
"Let's play," he said.

Ryan and Rabbit kicked the football between the trees. They ran, laughed, and tried tricky moves under the tall canopy of leaves. After a while, they sat down to rest.

Ryan pulled a book out of his backpack. "Time for some reading," he said.
Rabbit leaned closer. Ryan read a story about a clever bird who solved puzzles to protect its nest. As he read aloud, a quiet hoot echoed from the branches above. An owl perched on a high limb, watching them silently. Ryan noticed it and smiled but didn't know its name or why it followed him.

Later, Ryan pulled out his tiny camp stove and a lunchbox. "Let's do some cooking," he said.
He and Rabbit gathered a few forest berries and mixed them with nuts. Using a small pan, they made a warm berry snack. The sweet smell drifted through the trees.

Suddenly, the bushes rustled. Out stepped a large tiger, golden-eyed with bold black stripes. He sniffed the air.
"I smell something tasty," the tiger said, stepping closer.

Ryan stood up slowly and stayed calm. He reached into his backpack and took out his sketchbook.
"Would you like to see some art?" he asked.

The tiger paused. Ryan flipped the pages and showed him a drawing. It was a picture of a tiger standing by a river, drawn in bright colors.

"That's me," the tiger said, his voice softer now.
"Yes. I saw your paw prints here last week," Ryan replied.
The tiger looked at the drawing again, then nodded slowly. Without another word, he turned and walked back into the trees.

Rabbit looked at Ryan. "That was close."
Ryan smiled. "Sometimes art speaks louder than words."
As the sun dipped lower, Ryan packed his things. He waved at the owl, gave Rabbit a high five, and started walking home.

His journal now held notes about football, reading, cooking, and art. But more than that, it held the memories of a day spent being brave, kind, and curious.
Ryan, the Forest Explorer, couldn't wait to return the next day.`;

const DRAW_TELL_PREVIEW_TITLE = `Johnny's 5th Birthday Gift`;
const DRAW_TELL_PREVIEW_TEXT = `Johnny woke up bright and early. The sun peeked through his curtains, and a big grin spread across his face. Today marked his 5th birthday, and he could already feel the excitement bubbling inside him. He rushed to the living room and gasped. The whole place looked magical. Colorful balloons floated near the ceiling. Streamers curled through the air. A row of bright stars danced along the walls, and a banner overhead shouted, "HAPPY BIRTHDAY!" in big, bold letters.

Johnny wore his party hat proudly. He bounced in his new blue shirt and red shorts, his eyes wide as he looked around. In front of him sat a huge chocolate cake with three glowing candles and two taller ones, one for every year of his life. His mom lit the candles and said, "Make a big wish, Johnny!"

He squeezed his eyes shut, thought really hard, then blew out all five flames in one strong puff. Everyone clapped and cheered.

After cake came the best part, the presents. Brightly wrapped boxes waited beside the table. Johnny opened the first one. Inside, he found crayons and coloring books. He smiled and said thank you. The second gift had a stuffed animal, soft and cuddly. But the third gift made him freeze.

He tore the wrapping off and stared at a shiny red toy car with silver stripes and flashing lights. His mouth dropped open. "Wow!" he shouted. "This is the best one!"

He placed the car on the floor, pushed a button, and watched it zoom across the room. It made revving sounds and even flipped over little blocks like a real stunt car. Johnny chased after it, laughing louder than anyone.

All his friends joined him. They made a racetrack out of streamers, raced the toy car around the table, and clapped every time it zipped by.

At the end of the day, Johnny sat beside his cake, the toy car in one hand and a balloon string in the other. His cheeks hurt from smiling so much.

He looked at his parents and said, "This is my favorite birthday ever."

And as the stars outside began to twinkle, Johnny knew he would never forget turning five with balloons, gifts, cake, and the coolest toy car he had ever seen.`;

// Container is 768px wide. Figma design is 1366px wide. Scale = 768/1366 ≈ 0.562
// We keep fonts at minimum readable sizes but scale all structural dimensions.
const CONTAINER_W = 872;
const SIDEBAR_W   = 60;   // 80 * 0.75 — keeping sidebar generous
const HEADER_H    = 100;  // 136 * 0.73
const CONTENT_H   = `calc(100vh - ${HEADER_H}px)`;

// --- Icons: iconsax-react (Linear = outline/default, Bold = selected) ---

const DiceIcon = ({ variant = 'Linear', color = '#FFFFFF' }) => {
  const bold = variant === 'Bold';
  return bold ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="4" fill={color}/>
      <circle cx="8"  cy="8"  r="1.5" fill="#fcf1e8"/>
      <circle cx="16" cy="8"  r="1.5" fill="#fcf1e8"/>
      <circle cx="8"  cy="16" r="1.5" fill="#fcf1e8"/>
      <circle cx="16" cy="16" r="1.5" fill="#fcf1e8"/>
      <circle cx="12" cy="12" r="1.5" fill="#fcf1e8"/>
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="2.75" y="2.75" width="18.5" height="18.5" rx="3.25" stroke={color} strokeWidth="1.5"/>
      <circle cx="8"  cy="8"  r="1.25" fill={color}/>
      <circle cx="16" cy="8"  r="1.25" fill={color}/>
      <circle cx="8"  cy="16" r="1.25" fill={color}/>
      <circle cx="16" cy="16" r="1.25" fill={color}/>
      <circle cx="12" cy="12" r="1.25" fill={color}/>
    </svg>
  );
};

const PlusIcon = ({ color = '#262626' }) => (
  <svg width="33" height="33" viewBox="0 0 28 28" fill="none">
    <path d="M14 6v16M6 14h16" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

// --- Animation keyframes (injected once at mount) ---

function GlobalStyles() {
  return (
    <style>{`
      @keyframes modalSpringIn {
        0%   { transform: scale(0.8);   opacity: 0; }
        70%  { transform: scale(1.02);  opacity: 1; }
        100% { transform: scale(1);                 }
      }
      @keyframes modalFadeOut {
        0%   { transform: scale(1);    opacity: 1; }
        30%  { transform: scale(1.02); opacity: 1; }
        70%  { transform: scale(0.88); opacity: 1; }
        100% { transform: scale(0.8);  opacity: 0; }
      }
      @keyframes backdropFadeIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      @keyframes backdropFadeOut {
        from { opacity: 1; }
        to   { opacity: 0; }
      }
    `}</style>
  );
}

const PaletteIcon = ({ color = '#262626' }) => (
  <svg width="33" height="33" viewBox="0 0 28 28" fill="none">
    <path d="M14 4C8.477 4 4 8.477 4 14c0 5.523 4.477 10 10 10 1.105 0 2-.895 2-2v-1c0-.552.448-1 1-1h2c3.314 0 6-2.686 6-6 0-5.523-4.477-10-10-10z" stroke={color} strokeWidth="2" />
    <circle cx="9"  cy="13" r="1.5" fill={color} />
    <circle cx="13" cy="9"  r="1.5" fill={color} />
    <circle cx="18" cy="11" r="1.5" fill={color} />
    <circle cx="19" cy="16" r="1.5" fill={color} />
  </svg>
);

const ButterflyIcon = ({ color = '#262626' }) => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    {/* Upper left wing */}
    <path d="M16 14 C13 9, 5 6, 4 10 C3 15, 10 19, 16 19" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    {/* Upper right wing */}
    <path d="M16 14 C19 9, 27 6, 28 10 C29 15, 22 19, 16 19" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    {/* Lower left wing */}
    <path d="M16 19 C12 20, 6 22, 6 26 C6 29, 12 29, 16 24" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    {/* Lower right wing */}
    <path d="M16 19 C20 20, 26 22, 26 26 C26 29, 20 29, 16 24" stroke={color} strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
    {/* Body */}
    <path d="M16 12 C15.3 15, 15.3 20, 16 24" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none"/>
    {/* Antennae */}
    <path d="M16 12 C15 10, 13 8, 11 6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M16 12 C17 10, 19 8, 21 6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="11" cy="5.5" r="1.2" fill={color}/>
    <circle cx="21" cy="5.5" r="1.2" fill={color}/>
  </svg>
);

const HeadphoneIcon = ({ color = '#404040' }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <path d="M3 18v-6a9 9 0 0118 0v6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3v5zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3v5z" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const VideoPlayIcon = ({ color = '#404040' }) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.8"/>
    <path d="M10 8.5l6 3.5-6 3.5V8.5z" fill={color}/>
  </svg>
);

const NoteIcon = ({ color = '#262626' }) => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    {/* Document outline */}
    <rect x="6" y="3" width="20" height="26" rx="2.5" stroke={color} strokeWidth="1.5"/>
    {/* Text lines */}
    <path d="M10 11 H22" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10 16 H22" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10 21 H17" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// --- Data ---

const NAV_ITEMS = [
  { id: 'menu',      label: 'Menu',      Icon: HambergerMenu, forceVariant: 'Linear' },
  { id: 'home',      label: 'Home',      Icon: Home2 },
  { id: 'dice',      label: 'Dice',      Icon: DiceIcon },
  { id: 'library',   label: 'Library',   Icon: Book },
  { id: 'community', label: 'Community', Icon: People },
];

const MODE_CARDS = [
  { id: 'create', title: 'Create New Story', Icon: PlusIcon    },
  { id: 'draw',   title: 'Draw & Tell Mode', Icon: PaletteIcon },
];

// --- Sidebar ---

function NavItem({ item, active, onClick }) {
  const { Icon } = item;
  return (
    <button
      onClick={() => onClick(item.id)}
      title={item.label}
      className="flex items-center justify-center rounded-full transition-colors duration-150 focus:outline-none"
      style={{
        width: 36, height: 36,
        backgroundColor: active ? '#f4c8a3' : 'transparent',
        flexShrink: 0,
      }}
    >
      <Icon size="20" variant={item.forceVariant ?? (active ? 'Bold' : 'Linear')} color={active ? '#7a6451' : '#FFFFFF'} strokeWidth={1.5} />
    </button>
  );
}

function Sidebar({ activeNav, onNavChange, darkMode, onToggleDark }) {
  return (
    <aside
      className="flex flex-col items-center flex-shrink-0"
      style={{
        width: SIDEBAR_W,
        height: '100%',
        backgroundColor: '#B7967A',
        paddingTop: 16,
        paddingBottom: 16,
        overflow: 'hidden',
      }}
    >
      {/* Nav items */}
      <div className="flex flex-col flex-1 items-center" style={{ gap: 18 }}>
        {NAV_ITEMS.map(item => (
          <NavItem key={item.id} item={item} active={activeNav === item.id} onClick={onNavChange} />
        ))}
      </div>

      {/* Dark mode toggle */}
      <button
        onClick={onToggleDark}
        title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        className="flex items-center justify-center rounded-full flex-shrink-0 focus:outline-none"
        style={{ width: 36, height: 36, backgroundColor: darkMode ? '#7a6451' : '#fcf1e8', marginBottom: 40 }}
      >
        {darkMode
          ? <Moon size="20" variant="Bold"   color="#FFFFFF" />
          : <Sun  size={20} strokeWidth={1.5} color="#B7967A" />}
      </button>
    </aside>
  );
}

// --- Header ---

function Header({ title = 'Welcome Kiraz!', subtitle = "Every day holds a new story! Let's create one together.", onAvatarClick, mascotUrl = MASCOT_URL, mascotScale = 1, mascotOffsetY = 0, avatarUrl = AVATAR_URL }) {
  const theme = useContext(ThemeContext);
  const mw = Math.round(39 * mascotScale);
  const mh = Math.round(66 * mascotScale);
  return (
    <header
      className="flex items-center flex-shrink-0"
      style={{
        height: HEADER_H,
        backgroundColor: theme.bg200,
        paddingLeft: 24,
        paddingRight: 24,
        gap: 16,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Mascot — slightly taller than header, sits flush at bottom like Figma */}
      <div style={{ width: 39, height: 66, flexShrink: 0, overflow: 'visible' }}>
        <img
          src={mascotUrl}
          alt="Taleo mascot"
          style={{
            width: mw,
            height: mh,
            objectFit: 'contain',
            transform: mascotOffsetY !== 0 ? `translateY(${mascotOffsetY}px)` : undefined,
          }}
        />
      </div>

      {/* Text */}
      <div className="flex flex-col flex-1" style={{ gap: 3 }}>
        <h1
          className="font-bogart font-bold"
          style={{ color: theme.text100, fontSize: 22, lineHeight: '30px', margin: 0 }}
        >
          {title}
        </h1>
        <p
          className="font-satoshi font-normal"
          style={{ color: theme.text200, fontSize: 13, lineHeight: '18px', margin: 0 }}
        >
          {subtitle}
        </p>
      </div>

      {/* Avatar */}
      <img
        src={avatarUrl}
        alt="User avatar"
        onClick={onAvatarClick}
        style={{
          width: 46,
          height: 46,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          cursor: onAvatarClick ? 'pointer' : 'default',
        }}
      />
    </header>
  );
}

// --- Cards ---

function ModeCard({ card, onClick }) {
  const theme = useContext(ThemeContext);
  const { Icon, title } = card;
  return (
    <div
      onClick={onClick}
      className="flex flex-col items-center justify-center cursor-pointer"
      style={{
        width: 220,
        height: 175,
        backgroundColor: theme.cardBg,
        border: `2px solid ${theme.btnPrimaryBg}`,
        borderRadius: 18,
        gap: 20,
        flexShrink: 0,
      }}
    >
      <div
        className="flex items-center justify-center"
        style={{ width: 42, height: 42, borderRadius: 14, backgroundColor: theme.cardBg }}
      >
        <Icon color={theme.btnPrimaryBg} />
      </div>
      <p
        className="font-satoshi font-bold text-center"
        style={{ color: theme.text100, fontSize: 13, lineHeight: '18px', margin: 0, maxWidth: 160 }}
      >
        {title}
      </p>
    </div>
  );
}

// --- Story Type Card (used inside modal) ---

function StoryTypeCard({ icon, label, selected, onClick }) {
  const theme = useContext(ThemeContext);
  return (
    <div
      onClick={onClick}
      className="flex flex-col items-center justify-center cursor-pointer"
      style={{
        flex: 1,
        height: 156,
        borderRadius: 14,
        gap: 14,
        border: selected ? `2px solid ${theme.cardSelectedBorder}` : `2px solid ${theme.bg300}`,
        backgroundColor: selected ? theme.storyCardSelectedBg : theme.storyCardUnselectedBg,
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </div>
      <p
        className="font-satoshi font-semibold text-center"
        style={{ fontSize: 13, lineHeight: '18px', margin: 0, color: theme.text100 }}
      >
        {label}
      </p>
    </div>
  );
}

// --- Create Story Modal ---

function CreateStoryModal({ storyType, onSelectType, onClose, onNext, isClosing }) {
  const theme = useContext(ThemeContext);
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: isClosing
          ? 'backdropFadeOut 0.8s ease forwards'
          : 'backdropFadeIn 0.18s ease forwards',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 480,
          backgroundColor: theme.modalBg,
          borderRadius: 20,
          padding: '28px 28px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          boxShadow: '0 8px 40px rgba(7, 20, 24, 0.20)',
          animation: isClosing
            ? 'modalFadeOut 0.8s ease forwards'
            : 'modalSpringIn 0.7s ease forwards',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span
            className="font-satoshi font-bold"
            style={{ fontSize: 18, color: theme.text100, lineHeight: '24px' }}
          >
            Create New Story
          </span>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', cursor: 'pointer', borderRadius: 8,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M2 2L16 16M16 2L2 16" stroke={theme.modalCloseIcon} strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Story type cards */}
        <div style={{ display: 'flex', gap: 16 }}>
          <StoryTypeCard
            icon={<ButterflyIcon color={theme.text100} />}
            label="Free Form Story"
            selected={storyType === 'freeform'}
            onClick={() => onSelectType('freeform')}
          />
          <StoryTypeCard
            icon={<NoteIcon color={theme.text100} />}
            label="Personalised Story"
            selected={storyType === 'personalised'}
            onClick={() => onSelectType('personalised')}
          />
        </div>

        {/* Next button */}
        <button
          onClick={onNext}
          className="font-satoshi font-bold"
          style={{
            width: '100%', height: 48,
            backgroundColor: theme.btnPrimaryBg, color: theme.btnPrimaryText,
            border: 'none', borderRadius: 12, fontSize: 15, cursor: 'pointer',
            transition: 'background-color 0.15s, transform 0.1s ease',
          }}
          onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.97)'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          Next
        </button>
      </div>
    </div>
  );
}

// --- Story Dice Data ---

const DICE_POOL = {
  character: [
    'Farmer', 'Knight', 'Princess', 'Wizard', 'Explorer', 'Merchant',
    'Pirate', 'Astronaut', 'Chef', 'Doctor', 'Inventor', 'Dancer',
    'Painter', 'Sailor', 'Hunter', 'Blacksmith', 'Shepherd', 'Archer',
    'Poet', 'Alchemist', 'Ninja', 'Samurai', 'Viking', 'Monk',
    'Jester', 'Scholar', 'Ranger', 'Bard', 'Nomad', 'Sorcerer',
  ],
  sideCharacter: [
    'Fairy', 'Dragon', 'Wolf', 'Robot', 'Ghost', 'Witch',
    'Unicorn', 'Mermaid', 'Griffin', 'Phoenix', 'Goblin', 'Troll',
    'Sprite', 'Centaur', 'Pegasus', 'Serpent', 'Gnome', 'Imp',
    'Banshee', 'Kitsune', 'Selkie', 'Yeti', 'Djinn', 'Golem',
    'Nymph', 'Satyr', 'Chimera', 'Basilisk', 'Raven', 'Minotaur',
  ],
  theme: [
    'Forest', 'Castle', 'Ocean', 'Space', 'Mountain', 'Desert',
    'Volcano', 'Tundra', 'Swamp', 'Jungle', 'Cave', 'Island',
    'Ruins', 'Village', 'Skyship', 'Dungeon', 'Meadow', 'Glacier',
    'Labyrinth', 'Clockwork City', 'Haunted Manor', 'Sunken City',
    'Storm Cloud', 'Crystal Caves', 'Floating Isles', 'Dark Forest',
    'Ancient Temple', 'Hidden Valley', 'Frozen Tundra', 'Lava Fields',
  ],
  object: [
    'Axe', 'Magic Wand', 'Treasure Chest', 'Rainbow', 'Umbrella', 'Old Key',
    'Compass', 'Lantern', 'Mirror', 'Potion', 'Scroll', 'Flute',
    'Rope', 'Shield', 'Hourglass', 'Crystal Ball', 'Feather', 'Bell',
    'Map', 'Locket', 'Dagger', 'Spyglass', 'Candle', 'Net',
    'Gloves', 'Crown', 'Hammer', 'Chalice', 'Seed', 'Cloak',
  ],
};

function rollAll() {
  const pick = arr => arr[Math.floor(Math.random() * arr.length)];
  return {
    character:     pick(DICE_POOL.character),
    sideCharacter: pick(DICE_POOL.sideCharacter),
    theme:         pick(DICE_POOL.theme),
    object:        pick(DICE_POOL.object),
  };
}

// --- Dice Card ---

function DiceCard({ label, value, locked = false, onToggleLock, hasRolled = false }) {
  const theme = useContext(ThemeContext);
  return (
    <div style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
      <div
        onClick={hasRolled ? onToggleLock : undefined}
        style={{
          width: 220, height: 150,
          backgroundColor: '#b3c4cb',
          borderRadius: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          cursor: hasRolled ? 'pointer' : 'default',
          transition: 'opacity 0.2s',
          opacity: hasRolled && locked ? 0.88 : 1,
        }}
      >
        {value && (
          <span
            className="font-satoshi font-bold"
            style={{ color: '#151b1e', fontSize: 18, textAlign: 'center', padding: '0 12px', lineHeight: '26px' }}
          >
            {value}
          </span>
        )}
        {hasRolled && locked && (
          <div style={{ position: 'absolute', top: 8, right: 8 }}>
            <Lock size={20} variant="Bold" color="#151b1e" />
          </div>
        )}
      </div>
      <span className="font-satoshi font-bold" style={{ fontSize: 15, color: theme.text100, lineHeight: '24px' }}>
        {label}
      </span>
    </div>
  );
}

// --- Main Content ---

function MainContent({ onCreateClick, onDrawClick }) {
  const theme = useContext(ThemeContext);
  return (
    <main
      className="flex items-center justify-center"
      style={{
        flex: 1,
        overflow: 'hidden',
        backgroundColor: theme.bg100,
        gap: 20,
      }}
    >
      <div className="flex" style={{ gap: 20 }}>
        {MODE_CARDS.map(card => (
          <ModeCard key={card.id} card={card} onClick={
            card.id === 'create' ? onCreateClick :
            card.id === 'draw'   ? onDrawClick   :
            undefined
          } />
        ))}
      </div>
    </main>
  );
}

// --- Personalised Story Screen ---

const STORY_MODES_CONFIG = [
  { id: 'read',   label: 'Read',   Icon: ({ color }) => <Book size={17} variant="Linear" color={color} /> },
  { id: 'listen', label: 'Listen', Icon: HeadphoneIcon },
  { id: 'watch',  label: 'Watch',  Icon: VideoPlayIcon },
];

const ILLUSTRATIONS = [
  { id: 'disney',     label: 'Disney - Pixar 3D', src: ILLUS_DISNEY },
  { id: 'comic',      label: 'Comic book',         src: ILLUS_COMIC },
  { id: 'puppet',     label: 'Puppet',              src: ILLUS_PUPPET },
  { id: 'watercolor', label: 'Water color',         src: ILLUS_WATERCOLOR },
  { id: 'pencil',     label: 'Pencil Color',        src: ILLUS_PENCIL },
  { id: 'ghibli',     label: 'Studio Ghibli',       src: ILLUS_GHIBLI },
];

function FieldLabel({ children }) {
  const theme = useContext(ThemeContext);
  return (
    <span className="font-satoshi font-bold" style={{ fontSize: 14, color: theme.text100, lineHeight: '20px', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function ChevronDown() {
  return <ArrowDown2 size={14} color="#5d7b88" variant="Linear" />;
}

function KeywordField({ label, tags, input, onInputChange, onAdd, onRemove }) {
  const theme = useContext(ThemeContext);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FieldLabel>{label}</FieldLabel>
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap',
        border: `1.5px solid ${theme.inputBorder}`, borderRadius: 16,
        minHeight: 48, backgroundColor: theme.inputBg,
        padding: '8px 16px', gap: 8,
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1, alignItems: 'center' }}>
          {tags.map(tag => (
            <span key={tag} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              backgroundColor: theme.cardBg100, borderRadius: 4,
              padding: '4px 8px', fontSize: 14, color: theme.text100,
              fontFamily: 'Satoshi, sans-serif', fontWeight: 400,
            }}>
              {tag}
              <button onClick={() => onRemove(tag)} style={{
                background: theme.bg300, border: 'none', cursor: 'pointer',
                padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, width: 16, height: 16, borderRadius: '50%',
              }}>
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <path d="M6.5 1.5L1.5 6.5M1.5 1.5l5 5" stroke={theme.text200} strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </button>
            </span>
          ))}
          <input
            value={input}
            onChange={e => onInputChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }}
            placeholder="Add..."
            className="font-satoshi"
            style={{
              border: 'none', outline: 'none', fontSize: 14,
              color: theme.text100, backgroundColor: 'transparent', minWidth: 48,
            }}
          />
        </div>
      </div>
    </div>
  );
}

function Dropdown({ value, onChange, options, dropUp = false }) {
  const theme = useContext(ThemeContext);
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="font-satoshi"
        style={{
          width: '100%', height: 48, padding: '0 32px 0 16px',
          border: `1.5px solid ${theme.inputBorder}`, borderRadius: 16,
          backgroundColor: theme.inputBg, color: theme.text100,
          fontSize: 14, cursor: 'pointer', outline: 'none',
          display: 'flex', alignItems: 'center', textAlign: 'left',
          position: 'relative',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
        <span style={{
          position: 'absolute', right: 16, top: '50%', display: 'flex', pointerEvents: 'none',
          transform: open ? 'translateY(-50%) rotate(-180deg)' : 'translateY(-50%) rotate(0deg)',
          transition: 'transform 0.2s ease',
        }}>
          <ChevronDown />
        </span>
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          ...(dropUp
            ? { bottom: 'calc(100% + 4px)', top: 'auto' }
            : { top: 'calc(100% + 4px)', bottom: 'auto' }),
          left: 0, right: 0, zIndex: 100,
          backgroundColor: theme.cardBg, border: `1.5px solid ${theme.border}`, borderRadius: 16,
          overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {options.map(o => (
            <div
              key={o}
              onClick={() => { onChange(o); setOpen(false); }}
              className="font-satoshi"
              style={{
                padding: '8px 16px', fontSize: 14, cursor: 'pointer',
                color: o === value ? theme.btnPrimaryBg : theme.text100,
                backgroundColor: o === value ? theme.cardBg100 : theme.cardBg,
                fontWeight: o === value ? 600 : 400,
              }}
            >
              {o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, dropUp = false }) {
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FieldLabel>{label}</FieldLabel>
      <Dropdown value={value} onChange={onChange} options={options} dropUp={dropUp} />
    </div>
  );
}

function IllustrationOption({ id, label, src, selected, onSelect }) {
  const theme = useContext(ThemeContext);
  return (
    <div
      onClick={() => onSelect(id)}
      style={{ flex: 1, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}
    >
      <div style={{
        width: '100%', height: 112, borderRadius: 8,
        overflow: 'hidden', position: 'relative', backgroundColor: theme.cardBg100,
      }}>
        <img src={src} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        {selected && (
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)' }} />
        )}
        {selected && (
          <div style={{ position: 'absolute', bottom: 8, right: 8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect width="24" height="24" rx="6" fill="#495f6a"/>
              <path d="M6.5 12l4 4 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
      </div>
      <p className="font-satoshi" style={{ fontSize: 14, color: theme.text100, margin: 0, textAlign: 'center', lineHeight: '20px' }}>
        {label}
      </p>
    </div>
  );
}

function SecondaryButton({ onClick, children }) {
  const theme = useContext(ThemeContext);
  return (
    <button
      onClick={onClick}
      className="font-satoshi font-bold"
      style={{
        width: 120, padding: '16px', backgroundColor: theme.cardBg100,
        border: 'none', borderRadius: 16, fontSize: 14, color: theme.text200, cursor: 'pointer',
        transition: 'background-color 0.15s',
      }}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ onClick, children }) {
  const theme = useContext(ThemeContext);
  return (
    <button
      onClick={onClick}
      className="font-satoshi font-bold"
      style={{
        width: 120, padding: '16px', backgroundColor: theme.btnPrimaryBg,
        border: 'none', borderRadius: 16, fontSize: 14, color: theme.btnPrimaryText, cursor: 'pointer',
        transition: 'background-color 0.15s, transform 0.1s ease',
      }}
      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.97)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      {children}
    </button>
  );
}

function StoryModeButton({ id, label, Icon, active, onClick }) {
  const theme = useContext(ThemeContext);
  return (
    <button
      onClick={onClick}
      className="font-satoshi font-bold"
      style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, padding: '16px',
        backgroundColor: active ? theme.btnPrimaryBg : theme.cardBg100,
        color: active ? theme.btnPrimaryText : theme.text200,
        border: 'none', borderRadius: 100, cursor: 'pointer',
        fontSize: 14, transition: 'background-color 0.15s',
      }}
    >
      <Icon color={active ? theme.btnPrimaryText : theme.text200} />
      {label}
    </button>
  );
}

// --- Glass overlay buttons (for use over images / dark backgrounds) ---

function GlassBackButton({ onClick, size = 36 }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: size, height: size, borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.28)', border: 'none', flexShrink: 0,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(2px)', transition: 'background-color 0.15s',
      }}
    >
      <ArrowLeft2 size={Math.round(size * 0.44)} color="#FFFFFF" variant="Linear" />
    </button>
  );
}

function GlassKebabButton({ onClick, size = 36 }) {
  const d = Math.round(size * 0.44);
  return (
    <button
      onClick={onClick}
      style={{
        width: size, height: size, borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.28)', border: 'none', flexShrink: 0,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(2px)', transition: 'background-color 0.15s',
      }}
    >
      <svg width={d} height={d} viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="4"  r="1.5" fill="#FFFFFF" />
        <circle cx="9" cy="9"  r="1.5" fill="#FFFFFF" />
        <circle cx="9" cy="14" r="1.5" fill="#FFFFFF" />
      </svg>
    </button>
  );
}

function GlassCloseButton({ onClick, size = 36 }) {
  const d = Math.round(size * 0.44);
  return (
    <button
      onClick={onClick}
      style={{
        width: size, height: size, borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.28)', border: 'none', flexShrink: 0,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(2px)', transition: 'background-color 0.15s',
      }}
    >
      <svg width={d} height={d} viewBox="0 0 18 18" fill="none">
        <path d="M3 3L15 15M15 3L3 15" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    </button>
  );
}

function GlassFullscreenButton({ onClick, size = 36 }) {
  const d = Math.round(size * 0.44);
  return (
    <button
      onClick={onClick}
      style={{
        width: size, height: size, borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.28)', border: 'none', flexShrink: 0,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(2px)', transition: 'background-color 0.15s',
      }}
    >
      <svg width={d} height={d} viewBox="0 0 18 18" fill="none">
        <path d="M2 2h5M2 2v5M16 2h-5M16 2v5M2 16h5M2 16v-5M16 16h-5M16 16v-5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function ModifyPromptModal({ onClose, isClosing }) {
  const theme = useContext(ThemeContext);
  const [prompt, setPrompt] = React.useState('');

  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: isClosing
          ? 'backdropFadeOut 0.8s ease forwards'
          : 'backdropFadeIn 0.18s ease forwards',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: 460,
          backgroundColor: theme.cardBg,
          borderRadius: 20,
          padding: '28px 28px 24px',
          display: 'flex', flexDirection: 'column', gap: 24,
          boxShadow: '0 8px 40px rgba(7,20,24,0.20)',
          zoom: 0.9,
          animation: isClosing
            ? 'modalFadeOut 0.8s ease forwards'
            : 'modalSpringIn 0.7s ease forwards',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Title */}
        <span className="font-satoshi font-bold" style={{ fontSize: 15, color: theme.text100, lineHeight: '22px' }}>
          Modify with a Prompt
        </span>

        {/* Textarea + mic button */}
        <div style={{ position: 'relative' }}>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="Describe how you'd like to modify the story..."
            className="font-satoshi"
            style={{
              width: '100%', height: 130, padding: '14px 16px',
              boxSizing: 'border-box', resize: 'none', outline: 'none',
              backgroundColor: theme.cardBg100, border: 'none', borderRadius: 14,
              fontSize: 13, color: theme.text100, lineHeight: '20px',
            }}
          />
          <button
            style={{
              position: 'absolute', bottom: 10, right: 10,
              width: 36, height: 36, borderRadius: '50%',
              backgroundColor: theme.cardBg, border: `1px solid ${theme.border}`,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background-color 0.15s',
            }}
            title="Voice input"
          >
            <Microphone2 size={16} variant="Linear" color={theme.text400} />
          </button>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            variant="tertiary"
            onClick={onClose}
            style={{ flex: 1, padding: '14px', borderRadius: 14, fontSize: 14 }}
          >
            Cancel
          </Button>
          <button
            className="font-satoshi font-bold"
            style={{
              flex: 1, padding: '14px',
              backgroundColor: theme.btnPrimaryBg, border: 'none',
              borderRadius: 14, fontSize: 14, color: theme.btnPrimaryText, cursor: 'pointer',
              transition: 'background-color 0.15s, transform 0.1s ease',
            }}
            onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.97)'; }}
            onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            Regenerate
          </button>
        </div>
      </div>
    </div>
  );
}

const READ_INLINE_IMG_1 = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960921/main_story1_image_2_r0wlrb.webp';
const READ_INLINE_IMG_2 = 'https://res.cloudinary.com/dcnujnpbm/image/upload/q_auto/f_auto/v1776960924/main_story1_image_3_kgrgjs.webp';

function StoryReadScreen({ onBack, story, onListen, onWatch, previewOrigin }) {
  const theme = useContext(ThemeContext);
  const sc = (n) => Math.round(n * 0.63);

  const isDrawTell = previewOrigin === 'draw-tell';

  const heroImage  = isDrawTell ? DRAW_TELL_READ_HERO_URL : (story?.image      || STORY_READ_HERO_URL);
  const inlineImg1 = isDrawTell ? DRAW_TELL_READ_IMG1_URL : (story?.inlineImg1 || READ_INLINE_IMG_1);
  const inlineImg2 = isDrawTell ? DRAW_TELL_READ_IMG2_URL : (story?.inlineImg2 || READ_INLINE_IMG_2);
  const title     = isDrawTell ? "Johnny's 5th Birthday Gift" : (story?.title    || 'Ryan the Forest Explorer');
  const category  = story?.category || 'User-Generated Stories';
  const ageGroup  = story?.ageGroup || '3 to 5 year old';
  const heroObjectPosition = title === "Mom's Messy Pancakes" ? 'center calc(35% - 60px)' : title === 'The Burnt Toast Plan' ? 'center calc(35% - 40px)' : 'center 35%';

  const bodyStyle = { fontFamily: "'Satoshi', sans-serif", fontWeight: 400, fontSize: sc(20), lineHeight: `${sc(32)}px`, color: theme.text100, margin: 0 };

  return (
    <main style={{ flex: 1, overflowY: 'auto', backgroundColor: theme.cardBg, display: 'flex', flexDirection: 'column' }}>

      {/* ── Hero image ── */}
      <div style={{ position: 'relative', margin: `${sc(56)}px ${sc(58)}px 0`, borderRadius: sc(40), overflow: 'hidden', flexShrink: 0, height: sc(400) }}>
        <img src={heroImage} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: heroObjectPosition, display: 'block' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(78,65,54,0) 40%, rgba(0,0,0,0.55) 100%)' }} />

        {/* Title + category */}
        <div style={{ position: 'absolute', bottom: sc(40), left: sc(40), right: sc(80) }}>
          <h2 className="font-satoshi" style={{ color: '#FFFFFF', fontSize: sc(40), fontWeight: 900, margin: 0, letterSpacing: '0.4px', lineHeight: 1.4 }}>{title}</h2>
          <p  className="font-satoshi" style={{ color: '#D9D9D9', fontSize: sc(18), fontWeight: 400, margin: `${sc(4)}px 0 0`, letterSpacing: '0.18px', lineHeight: 1.6 }}>{category} | Age Group: {ageGroup}</p>
        </div>

        {/* Back button */}
        <button onClick={onBack} style={{ position: 'absolute', top: sc(32), left: sc(32), width: sc(48), height: sc(48), borderRadius: sc(24), backgroundColor: 'rgba(0,0,0,0.3)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)', flexShrink: 0 }}>
          <ArrowLeft2 size={sc(24)} color="#FFFFFF" variant="Linear" />
        </button>

        {/* Kebab button */}
        <button style={{ position: 'absolute', top: sc(32), right: sc(32), width: sc(48), height: sc(48), borderRadius: sc(24), backgroundColor: 'rgba(0,0,0,0.3)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)', flexShrink: 0 }}>
          <svg width={sc(24)} height={sc(24)} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="5" r="1.8" fill="#FFFFFF"/><circle cx="12" cy="12" r="1.8" fill="#FFFFFF"/><circle cx="12" cy="19" r="1.8" fill="#FFFFFF"/></svg>
        </button>
      </div>

      {/* ── Story body ── */}
      <div style={{ padding: `${sc(48)}px ${sc(58)}px 60px`, display: 'flex', flexDirection: 'column', gap: sc(64) }}>

        {isDrawTell ? (
          <>
            {/* Section 1: opening paragraph */}
            <p style={bodyStyle}>Johnny woke up bright and early on the morning of his fifth birthday. He jumped out of bed, ran to the window, and looked outside. The sun was shining and the sky was full of fluffy white clouds. He could smell something sweet coming from the kitchen. His mom was baking his favorite chocolate cake.</p>

            {/* Section 2: text left + image right */}
            <div style={{ display: 'flex', gap: sc(48), alignItems: 'stretch' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={bodyStyle}>Johnny wore his party hat proudly. He bounced in his new blue shirt and red shorts, his eyes wide as he looked around. In front of him sat a huge chocolate cake with five glowing candles. His mom lit the candles and said, "Make a big wish, Johnny!"<br/><br/>He squeezed his eyes shut, thought really hard, then blew out all five flames in one strong puff. Everyone clapped and cheered.<br/><br/>After cake came the best part — the presents. Brightly wrapped boxes waited beside the table. Johnny opened the first one. Inside, he found crayons and coloring books. He smiled and said thank you. The second gift had a stuffed animal, soft and cuddly.</p>
              </div>
              <div style={{ flexShrink: 0, width: '40%', height: sc(380), borderRadius: sc(32), overflow: 'hidden', alignSelf: 'center' }}>
                <img src={inlineImg1} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 60%', display: 'block' }} />
              </div>
            </div>

            {/* Section 3: full-width toy car paragraph */}
            <p style={bodyStyle}>But the third gift made him freeze. He tore the wrapping off and stared at a shiny red toy car with silver stripes and flashing lights. His mouth dropped open. "Wow!" he shouted. "This is the best one!" He placed the car on the floor, pushed a button, and watched it zoom across the room. It made revving sounds and even flipped over little blocks like a real stunt car. Johnny chased after it, laughing louder than anyone.</p>

            {/* Section 4: image left + text right */}
            <div style={{ display: 'flex', gap: sc(48), alignItems: 'stretch' }}>
              <div style={{ flexShrink: 0, width: '48%', borderRadius: sc(32), overflow: 'hidden', alignSelf: 'stretch' }}>
                <img src={inlineImg2} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={bodyStyle}>All his friends joined him. They made a racetrack out of streamers, raced the toy car around the table, and clapped every time it zipped by.<br/><br/>At the end of the day, Johnny sat beside his cake, the toy car in one hand and a balloon string in the other. His cheeks hurt from smiling so much.<br/><br/>He looked at his parents and said, "This is my favorite birthday ever."<br/><br/>And as the stars outside began to twinkle, Johnny knew he would never forget turning five with balloons, gifts, cake, and the coolest toy car he had ever seen.</p>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Section 1: opening paragraph */}
            <p style={bodyStyle}>Ryan, a curious 6-year-old boy, loved exploring the forest behind his house. He wore a wide-brimmed hat, carried a small backpack, and proudly called himself the Forest Explorer. Every morning after breakfast, he grabbed his journal and walked into the woods with a big smile on his face. One sunny day, Ryan discovered a new path filled with tall trees, colorful leaves, and birdsong in the air. As he followed the trail, he heard a soft hopping sound behind him. It was Rabbit, his cheerful friend with floppy ears and a twitchy nose.</p>

            {/* Section 2: text left + image right */}
            <div style={{ display: 'flex', gap: sc(48), alignItems: 'stretch' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={bodyStyle}>"Ready for a new adventure?" Rabbit asked.<br/>"Always," Ryan said with a grin.<br/><br/>They wandered deeper into the forest. Along the way, Ryan spotted something near a tree stump. It was a perfectly round, clean football. He picked it up and bounced it on the ground.<br/>"Let's play," he said.<br/><br/>Ryan and Rabbit kicked the football between the trees. They ran, laughed, and tried tricky moves under the tall canopy of leaves. After a while, they sat down to rest.<br/><br/>Ryan pulled a book out of his backpack. "Time for some reading," he said.</p>
              </div>
              <div style={{ flexShrink: 0, width: '43%', borderRadius: sc(40), overflow: 'hidden', alignSelf: 'stretch', minHeight: sc(280) }}>
                <img src={inlineImg1} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
            </div>

            {/* Section 3: full-width continuation */}
            <p style={bodyStyle}>Rabbit leaned closer. Ryan read a story about a clever bird who solved puzzles to protect its nest. As he read aloud, a quiet hoot echoed from the branches above. An owl perched on a high limb, watching them silently. Ryan noticed it and smiled but didn't know its name or why it followed him.</p>

            {/* Section 4: image left + text right */}
            <div style={{ display: 'flex', gap: sc(48), alignItems: 'stretch' }}>
              <div style={{ flexShrink: 0, width: '55%', borderRadius: sc(40), overflow: 'hidden', alignSelf: 'stretch', minHeight: sc(280) }}>
                <img src={inlineImg2} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={bodyStyle}>Later, Ryan pulled out his tiny camp stove and a lunchbox. "Let's do some cooking," he said.<br/>He and Rabbit gathered a few forest berries and mixed them with nuts. Using a small pan, they made a warm berry snack. The sweet smell drifted through the trees.<br/><br/>Suddenly, the bushes rustled. Out stepped a large tiger, golden-eyed with bold black stripes. He sniffed the air.<br/>"I smell something tasty," the tiger said, stepping closer.<br/><br/>Ryan stood up slowly and stayed calm. He reached into his backpack and took out his sketchbook.<br/>"Would you like to see some art?" he asked.<br/><br/>The tiger paused. Ryan flipped the pages and showed him a drawing. It was a picture of a tiger standing by a river, drawn in bright colors.</p>
              </div>
            </div>

            {/* Section 5: closing paragraph */}
            <p style={bodyStyle}>"That's me," the tiger said, his voice softer now.<br/>"Yes. I saw your paw prints here last week," Ryan replied.<br/>The tiger looked at the drawing again, then nodded slowly. Without another word, he turned and walked back into the trees.<br/><br/>Rabbit looked at Ryan. "That was close."<br/>Ryan smiled. "Sometimes art speaks louder than words."<br/>As the sun dipped lower, Ryan packed his things. He waved at the owl, gave Rabbit a high five, and started walking home.<br/><br/>His journal now held notes about football, reading, cooking, and art. But more than that, it held the memories of a day spent being brave, kind, and curious. Ryan, the Forest Explorer, couldn't wait to return the next day.</p>

            {/* Moral of the Story */}
            <div style={{ backgroundColor: theme.moralBoxBg, borderRadius: sc(20), padding: sc(24), display: 'flex', flexDirection: 'column', gap: sc(5) }}>
              <p className="font-satoshi font-bold" style={{ fontSize: sc(24), lineHeight: `${sc(32)}px`, color: theme.text200, margin: 0 }}>Moral of the Story</p>
              <p className="font-satoshi"           style={{ fontSize: sc(20), lineHeight: `${sc(32)}px`, color: theme.text200, margin: 0, fontWeight: 400 }}>Curiosity and kindness make every adventure special. When one stays calm and uses their heart, even scary moments can turn into beautiful learning experiences.</p>
            </div>
          </>
        )}

        {/* Listen + Watch buttons */}
        <div style={{ display: 'flex', gap: sc(24), justifyContent: 'center' }}>
          <Button variant="secondary" scale={0.75} onClick={onListen} style={{ flex: 1, maxWidth: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: sc(12) }}>
            <HeadphoneIcon color={theme.text200} />
            Listen to this story
          </Button>
          <Button variant="secondary" scale={0.75} onClick={onWatch} style={{ flex: 1, maxWidth: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: sc(12) }}>
            <VideoPlayIcon color={theme.text200} />
            Watch this story
          </Button>
        </div>

      </div>
    </main>
  );
}

function StoryPreviewScreen({ onBack, onModify, onPublish, previewOrigin }) {
  const theme = useContext(ThemeContext);
  const sc = (n) => Math.round(n * 0.63);
  const mainRef = React.useRef(null);

  const isDrawTell = previewOrigin === 'draw-tell';
  const coverUrl   = isDrawTell ? DRAW_TELL_COVER_URL : STORY_COVER_URL;
  const title      = isDrawTell ? DRAW_TELL_PREVIEW_TITLE : 'Ryan the Forest Explorer';
  const storyText  = isDrawTell ? DRAW_TELL_PREVIEW_TEXT : STORY_PREVIEW_TEXT;

  const handleModify = () => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
    onModify();
  };

  return (
    <main ref={mainRef} style={{ flex: 1, overflowY: 'auto', backgroundColor: theme.cardBg, display: 'flex', flexDirection: 'column' }}>

      {/* Thumbnail */}
      <div style={{ position: 'relative', margin: `${sc(56)}px ${sc(58)}px 0`, borderRadius: sc(40), overflow: 'hidden', flexShrink: 0, height: sc(400) }}>
        <img
          src={coverUrl}
          alt="Story cover"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 35%', display: 'block' }}
        />
        {/* Gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(78,65,54,0) 40%, rgba(2,2,2,0.55))' }} />

        {/* Title + subtitle */}
        <div style={{ position: 'absolute', bottom: sc(40), left: sc(40), right: sc(80) }}>
          <h2
            className="font-satoshi"
            style={{ color: '#FFFFFF', fontSize: sc(40), fontWeight: 900, margin: 0, letterSpacing: '0.4px', lineHeight: 1.4 }}
          >
            {title}
          </h2>
          <p
            className="font-satoshi"
            style={{ color: '#D9D9D9', fontSize: 12, margin: `${sc(4)}px 0 0`, lineHeight: '18px', letterSpacing: '0.15px' }}
          >
            User-Generated Stories | Age Group: 3 to 5 year old
          </p>
        </div>

        {/* Back button */}
        <button
          onClick={onBack}
          style={{
            position: 'absolute', top: sc(32), left: sc(32),
            width: sc(48), height: sc(48), borderRadius: sc(24),
            backgroundColor: 'rgba(0,0,0,0.3)', border: 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(2px)', flexShrink: 0,
          }}
        >
          <ArrowLeft2 size={sc(24)} color="#FFFFFF" variant="Linear" />
        </button>

        {/* Story Preview chip */}
        <div style={{
          position: 'absolute', top: sc(32), right: sc(32),
          backgroundColor: theme.cardBg, borderRadius: sc(12),
          padding: `${sc(6)}px ${sc(14)}px`,
        }}>
          <span
            className="font-satoshi"
            style={{ fontSize: 10, fontWeight: 500, color: theme.text100, letterSpacing: 1, textTransform: 'uppercase' }}
          >
            Story Preview
          </span>
        </div>
      </div>

      {/* Story text */}
      <div style={{ padding: `${sc(40)}px ${sc(58)}px 0`, flex: 1 }}>
        {storyText.split('\n').map((para, i) => (
          para === ''
            ? <div key={i} style={{ height: sc(20) }} />
            : <p
                key={i}
                className="font-satoshi"
                style={{ fontSize: 13, lineHeight: '20px', color: theme.text100, margin: 0 }}
              >
                {para}
              </p>
        ))}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 12, padding: `40px ${sc(58)}px 60px`, flexShrink: 0, justifyContent: 'center' }}>
        <Button
          variant="tertiary"
          onClick={handleModify}
          style={{ width: 180, flexShrink: 0, padding: '14px', borderRadius: 14, fontSize: 14 }}
        >
          Modify
        </Button>
        <button
          className="font-satoshi font-bold"
          style={{
            width: 180, flexShrink: 0, padding: '14px', backgroundColor: theme.btnPrimaryBg,
            border: 'none', borderRadius: 14, fontSize: 14, color: theme.btnPrimaryText,
            cursor: 'pointer', transition: 'background-color 0.15s, transform 0.1s ease',
          }}
          onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.98)'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          onClick={onPublish}
        >
          Publish
        </button>
      </div>

    </main>
  );
}

function SectionHeading({ children }) {
  const theme = useContext(ThemeContext);
  return (
    <span className="font-satoshi font-bold" style={{ fontSize: 16, color: theme.text100, lineHeight: '28px' }}>
      {children}
    </span>
  );
}

function InputTextField({ label, value, onChange, placeholder }) {
  const theme = useContext(ThemeContext);
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FieldLabel>{label}</FieldLabel>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="font-satoshi"
        style={{
          height: 48, padding: '0 16px', boxSizing: 'border-box', width: '100%',
          border: `1.5px solid ${theme.inputBorder}`, borderRadius: 16,
          backgroundColor: theme.inputBg, color: theme.text100,
          fontSize: 14, outline: 'none',
        }}
      />
    </div>
  );
}

function PersonalisedStoryStep2Screen({ onBack, onGenerate }) {
  const theme = useContext(ThemeContext);
  const [mainName,    setMainName]    = useState('');
  const [mainAge,     setMainAge]     = useState('Select age');
  const [mainGender,  setMainGender]  = useState('Select Gender');
  const [mainRole,    setMainRole]    = useState('Select Role');
  const [storyTheme,  setStoryTheme]  = useState('Choose the theme for your story');
  const [addDesc,     setAddDesc]     = useState('');
  const [sideChars,   setSideChars]   = useState([
    { id: 1, name: '', type: 'Select character type', role: 'Select role' },
  ]);

  const updateSide = (id, field, val) =>
    setSideChars(prev => prev.map(c => c.id === id ? { ...c, [field]: val } : c));

  const removeSideChar = (id) =>
    setSideChars(prev => prev.filter(c => c.id !== id));

  const addSideChar = () =>
    setSideChars(prev => [...prev, { id: Date.now(), name: '', type: 'Select character type', role: 'Select role' }]);

  return (
    <main style={{ flex: 1, overflowY: 'auto', backgroundColor: theme.cardBg, position: 'relative' }}>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          position: 'absolute', top: 18, left: 18, zIndex: 10,
          width: 36, height: 36, borderRadius: '50%',
          backgroundColor: 'rgba(0,0,0,0.28)', border: 'none',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(2px)', transition: 'background-color 0.15s',
        }}
      >
        <ArrowLeft2 size={16} color="#FFFFFF" variant="Linear" />
      </button>

      <div style={{ padding: '36px 144px', display: 'flex', flexDirection: 'column', gap: 40, zoom: 0.9 }}>

        {/* Main Character Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <SectionHeading>Main Character Details</SectionHeading>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Name + Age */}
            <div style={{ display: 'flex', gap: 16, borderTop: '1.5px solid #95acb6', paddingTop: 16 }}>
              <InputTextField label="Name" value={mainName} onChange={setMainName} placeholder="Enter name" />
              <SelectField
                label="Age"
                value={mainAge}
                onChange={setMainAge}
                options={['3-4 years', '5-6 years', '7-8 years', '9-10 years', '11-12 years']}
              />
            </div>
            {/* Gender + Role */}
            <div style={{ display: 'flex', gap: 16 }}>
              <SelectField
                label="Gender"
                value={mainGender}
                onChange={setMainGender}
                options={['Boy', 'Girl', 'Non-binary']}
              />
              <SelectField
                label="Role"
                value={mainRole}
                onChange={setMainRole}
                options={['Hero', 'Sidekick', 'Villain', 'Mentor']}
              />
            </div>
          </div>
        </div>

        {/* Side Character Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <SectionHeading>Side Character Details <span style={{ fontWeight: 400 }}>(optional)</span></SectionHeading>
          {sideChars.map(char => (
            <div key={char.id} style={{ borderTop: '1.5px solid #95acb6', paddingTop: 16 }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                <InputTextField
                  label="Name"
                  value={char.name}
                  onChange={v => updateSide(char.id, 'name', v)}
                  placeholder="Enter name"
                />
                <SelectField
                  label="Type"
                  value={char.type}
                  onChange={v => updateSide(char.id, 'type', v)}
                  options={['Human', 'Animal', 'Mythical creature', 'Robot']}
                />
                <SelectField
                  label="Role"
                  value={char.role}
                  onChange={v => updateSide(char.id, 'role', v)}
                  options={['Hero', 'Sidekick', 'Villain', 'Mentor']}
                />
                {sideChars.length > 1 && (
                  <button
                    onClick={() => removeSideChar(char.id)}
                    style={{
                      flexShrink: 0, width: 32, height: 32, marginBottom: 8,
                      borderRadius: '50%', border: 'none',
                      backgroundColor: theme.cardBg100, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'background-color 0.15s',
                    }}
                    title="Remove character"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M1 1l10 10M11 1L1 11" stroke={theme.text400} strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
          {/* Add Side Character */}
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
            <button
              onClick={addSideChar}
              className="font-satoshi font-bold"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 24px', backgroundColor: theme.cardBg100,
                border: 'none', borderRadius: 16, fontSize: 14,
                color: theme.text200, cursor: 'pointer', transition: 'background-color 0.15s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke={theme.text200} strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Add New Side Character
            </button>
          </div>
        </div>

        {/* Story Theme + Additional Description */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <SelectField
            label="Story Theme"
            value={storyTheme}
            onChange={setStoryTheme}
            options={['Adventure', 'Fantasy', 'Mystery', 'Educational', 'Comedy', 'Friendship']}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <FieldLabel>Additional Description (optional)</FieldLabel>
            <textarea
              value={addDesc}
              onChange={e => setAddDesc(e.target.value)}
              placeholder="Additional details for your story..."
              className="font-satoshi"
              style={{
                height: 120, padding: '12px 16px', boxSizing: 'border-box', width: '100%',
                border: `1.5px solid ${theme.inputBorder}`, borderRadius: 16,
                backgroundColor: theme.cardBg100, color: theme.text100,
                fontSize: 14, outline: 'none', resize: 'none', lineHeight: '20px',
              }}
            />
          </div>
        </div>

        {/* Generate Preview */}
        <button
          className="font-satoshi font-bold"
          style={{
            width: '100%', height: 52,
            backgroundColor: theme.btnPrimaryBg, color: theme.btnPrimaryText,
            border: 'none', borderRadius: 16, fontSize: 14,
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8, marginBottom: 16,
            transition: 'background-color 0.15s, transform 0.1s ease',
          }}
          onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.99)'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          onClick={onGenerate}
        >
          <Magicpen size={17} variant="Linear" color={theme.btnPrimaryText} />
          Generate Preview
        </button>

      </div>
    </main>
  );
}

function PersonalisedStoryScreen({ onBack, onNext }) {
  const theme = useContext(ThemeContext);
  const [storyMode,         setStoryMode]         = useState('read');
  const [language,          setLanguage]          = useState('English');
  const [narrationVoice,    setNarrationVoice]    = useState('Smith (Male AI Voice)');
  const [storyLength,       setStoryLength]       = useState('2-5 min');
  const [focusTags,         setFocusTags]         = useState(['Football', 'Cooking', 'Art', 'Reading']);
  const [focusInput,        setFocusInput]        = useState('');
  const [avoidTags,         setAvoidTags]         = useState(['Bully', 'Beat', 'Tease', 'Quarrel']);
  const [avoidInput,        setAvoidInput]        = useState('');
  const [illustrationStyle, setIllustrationStyle] = useState('watercolor');

  const addTag    = (tags, setTags, input, setInput) => {
    const t = input.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setInput('');
  };
  const removeTag = (tags, setTags, tag) => setTags(tags.filter(t => t !== tag));

  const isListen = storyMode === 'listen';
  const isWatch  = storyMode === 'watch';
  const showNarrationVoice    = isListen || isWatch;
  const showIllustrationStyle = !isListen;

  return (
    <main style={{ flex: 1, overflowY: 'auto', backgroundColor: theme.cardBg, padding: '29px 144px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32, zoom: 0.9 }}>

        {/* Story Mode */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <FieldLabel>Story mode</FieldLabel>
          <div style={{ display: 'flex', gap: 8 }}>
            {STORY_MODES_CONFIG.map(mode => (
              <StoryModeButton
                key={mode.id}
                {...mode}
                active={storyMode === mode.id}
                onClick={() => setStoryMode(mode.id)}
              />
            ))}
          </div>
        </div>

        {/* Fields — top border acts as divider */}
        <div style={{ display: 'flex', gap: 16, borderTop: '1.5px solid #95acb6', paddingTop: 16, alignItems: 'flex-start', minHeight: 92 }}>
          <SelectField
            label="Language"
            value={language}
            onChange={setLanguage}
            options={['English', 'Spanish', 'French', 'German']}
          />
          <div style={{
            flex: 'none',
            width: showNarrationVoice ? 'calc((100% - 32px) / 3)' : '0px',
            minWidth: 0,
            overflow: 'visible',
            opacity: showNarrationVoice ? 1 : 0,
            marginLeft: showNarrationVoice ? 0 : -16,
            transition: showNarrationVoice
              ? 'width 0.5s ease, opacity 0.3s ease, margin-left 0.5s ease'
              : 'width 0.4s ease, opacity 0.3s ease, margin-left 0.4s ease',
          }}>
            <SelectField
              label="Narration Voice"
              value={narrationVoice}
              onChange={setNarrationVoice}
              options={['Smith (Male AI Voice)', 'Emma (Female AI Voice)', 'Aria (Female AI Voice)']}
            />
          </div>
          <SelectField
            label="Story Length"
            value={storyLength}
            onChange={setStoryLength}
            options={['2-5 min', '5-10 min', '10-15 min']}
          />
        </div>

        {/* Keywords to focus */}
        <KeywordField
          label="Keywords (to focus)"
          tags={focusTags} input={focusInput}
          onInputChange={setFocusInput}
          onAdd={() => addTag(focusTags, setFocusTags, focusInput, setFocusInput)}
          onRemove={tag => removeTag(focusTags, setFocusTags, tag)}
        />

        {/* Keywords to avoid */}
        <KeywordField
          label="Keywords (to avoid)"
          tags={avoidTags} input={avoidInput}
          onInputChange={setAvoidInput}
          onAdd={() => addTag(avoidTags, setAvoidTags, avoidInput, setAvoidInput)}
          onRemove={tag => removeTag(avoidTags, setAvoidTags, tag)}
        />

        {/* Illustration Style — Read + Watch modes */}
        {showIllustrationStyle && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            <div style={{ borderTop: `1.5px solid ${theme.border}`, paddingTop: 16 }}>
              <FieldLabel>Illustration style</FieldLabel>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[ILLUSTRATIONS.slice(0, 3), ILLUSTRATIONS.slice(3)].map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: 8 }}>
                  {row.map(item => (
                    <IllustrationOption
                      key={item.id}
                      {...item}
                      selected={illustrationStyle === item.id}
                      onSelect={setIllustrationStyle}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Back + Next */}
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 32, paddingBottom: 32 }}>
          <Button variant="tertiary" onClick={onBack} style={{ width: 120, padding: '16px', borderRadius: 16, fontSize: 14 }}>Back</Button>
          <PrimaryButton onClick={() => onNext(storyMode)}>Next</PrimaryButton>
        </div>

      </div>
    </main>
  );
}

// --- Freeform Story Screen ---

const PROMPT_SUGGESTIONS = [
  'A dragon who forgot how to fly',
  'A sleepover in space',
  'First bike ride',
];

function FreeformStoryScreen({ onBack, onGenerate }) {
  const theme = useContext(ThemeContext);
  const [prompt,             setPrompt]             = useState('');
  const [storyMode,          setStoryMode]          = useState('read');
  const [language,           setLanguage]           = useState('English (default)');
  const [narrationVoice,     setNarrationVoice]     = useState('Smith (Male AI Voice)');
  const [storyLength,        setStoryLength]        = useState('2-5 min (default)');
  const [illustrationStyle,  setIllustrationStyle]  = useState('watercolor');

  const showNarrationVoice = storyMode === 'listen' || storyMode === 'watch';
  const showIllustration   = storyMode !== 'listen';

  return (
    <main style={{ flex: 1, overflowY: 'auto', backgroundColor: theme.cardBg, padding: '29px 144px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32, zoom: 0.9 }}>

        {/* Try Free-form Story */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <FieldLabel>Try Free-form Story</FieldLabel>
          <div style={{ position: 'relative' }}>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="Type your story prompt here..."
              className="font-satoshi"
              style={{
                width: '100%', height: 160, padding: '14px 16px',
                boxSizing: 'border-box', resize: 'none', outline: 'none',
                backgroundColor: theme.cardBg100, border: 'none', borderRadius: 16,
                fontSize: 14, color: theme.text100, lineHeight: '22px',
              }}
            />
            <button
              style={{
                position: 'absolute', bottom: 12, right: 12,
                width: 40, height: 40, borderRadius: '50%',
                backgroundColor: theme.cardBg, border: `1px solid ${theme.border}`,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background-color 0.15s',
              }}
              title="Voice input"
            >
              <Microphone2 size={18} variant="Linear" color={theme.text400} />
            </button>
          </div>
        </div>

        {/* Prompt suggestions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <FieldLabel>Need inspiration? Try a ready-made prompt!</FieldLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PROMPT_SUGGESTIONS.map(s => (
              <button
                key={s}
                onClick={() => setPrompt(s)}
                className="font-satoshi"
                style={{
                  padding: '10px 20px', backgroundColor: theme.cardBg100,
                  border: 'none', borderRadius: 10, fontSize: 14,
                  color: theme.text100, cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  fontWeight: prompt === s ? 600 : 400,
                  outline: prompt === s ? '2px solid #5d7b88' : 'none',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Story Mode */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1.5px solid #95acb6', paddingTop: 24 }}>
          <FieldLabel>Story mode</FieldLabel>
          <div style={{ display: 'flex', gap: 8 }}>
            {STORY_MODES_CONFIG.map(mode => (
              <StoryModeButton
                key={mode.id}
                {...mode}
                active={storyMode === mode.id}
                onClick={() => setStoryMode(mode.id)}
              />
            ))}
          </div>
        </div>

        {/* Language / Narration Voice / Story Length */}
        <div style={{ display: 'flex', gap: 16, borderTop: '1.5px solid #95acb6', paddingTop: 24, alignItems: 'flex-start', minHeight: 92 }}>
          <SelectField
            label="Language"
            value={language}
            onChange={setLanguage}
            options={['English (default)', 'Spanish', 'French', 'German']}
            dropUp={storyMode === 'listen'}
          />
          <div style={{
            flex: 'none',
            width: showNarrationVoice ? 'calc((100% - 32px) / 3)' : '0px',
            minWidth: 0,
            overflow: 'visible',
            opacity: showNarrationVoice ? 1 : 0,
            marginLeft: showNarrationVoice ? 0 : -16,
            transition: showNarrationVoice
              ? 'width 0.5s ease, opacity 0.3s ease, margin-left 0.5s ease'
              : 'width 0.4s ease, opacity 0.3s ease, margin-left 0.4s ease',
          }}>
            <SelectField
              label="Narration Voice"
              value={narrationVoice}
              onChange={setNarrationVoice}
              options={['Smith (Male AI Voice)', 'Emma (Female AI Voice)', 'Aria (Female AI Voice)']}
              dropUp={storyMode === 'listen'}
            />
          </div>
          <SelectField
            label="Story Length"
            value={storyLength}
            onChange={setStoryLength}
            options={['2-5 min (default)', '5-10 min', '10-15 min']}
            dropUp={storyMode === 'listen'}
          />
        </div>

        {/* Illustration Style */}
        {showIllustration && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FieldLabel>Illustration style</FieldLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[ILLUSTRATIONS.slice(0, 3), ILLUSTRATIONS.slice(3)].map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: 8 }}>
                  {row.map(item => (
                    <IllustrationOption
                      key={item.id}
                      {...item}
                      selected={illustrationStyle === item.id}
                      onSelect={setIllustrationStyle}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Generate Preview */}
        <button
          className="font-satoshi font-bold"
          style={{
            width: '100%', height: 52,
            backgroundColor: theme.btnPrimaryBg, color: theme.btnPrimaryText,
            border: 'none', borderRadius: 16, fontSize: 14,
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8, marginBottom: 16,
            transition: 'background-color 0.15s, transform 0.1s ease',
          }}
          onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.99)'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          onClick={() => onGenerate(storyMode)}
        >
          <Magicpen size={17} variant="Linear" color={theme.btnPrimaryText} />
          Generate Preview
        </button>

      </div>
    </main>
  );
}

// --- Story Dice Screen ---

function StoryDiceScreen({ onBack }) {
  const theme = useContext(ThemeContext);
  const [rolled, setRolled] = useState(null);
  const [display, setDisplay] = useState(null); // animating values shown on cards
  const [locked, setLocked] = useState({ character: false, sideCharacter: false, theme: false, object: false });
  const [isRolling, setIsRolling] = useState(false);
  const [history, setHistory] = useState({ character: [], sideCharacter: [], theme: [], object: [] });
  const [storyText, setStoryText] = useState('');
  const intervalRef = useRef(null);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const handleRoll = () => {
    if (isRolling) return;
    const pick = arr => arr[Math.floor(Math.random() * arr.length)];

    // Determine final values
    const isFirst = !rolled;
    const finalValues = {
      character:     (!isFirst && locked.character)     ? rolled.character     : pick(DICE_POOL.character),
      sideCharacter: (!isFirst && locked.sideCharacter) ? rolled.sideCharacter : pick(DICE_POOL.sideCharacter),
      theme:         (!isFirst && locked.theme)         ? rolled.theme         : pick(DICE_POOL.theme),
      object:        (!isFirst && locked.object)        ? rolled.object        : pick(DICE_POOL.object),
    };

    setIsRolling(true);
    const startTime = Date.now();
    const DURATION = 750;

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= DURATION) {
        clearInterval(intervalRef.current);
        setDisplay(finalValues);
        setRolled(finalValues);
        setHistory(prev => ({
          character:     (isFirst || !locked.character)     ? [...prev.character,     finalValues.character]     : prev.character,
          sideCharacter: (isFirst || !locked.sideCharacter) ? [...prev.sideCharacter, finalValues.sideCharacter] : prev.sideCharacter,
          theme:         (isFirst || !locked.theme)         ? [...prev.theme,         finalValues.theme]         : prev.theme,
          object:        (isFirst || !locked.object)        ? [...prev.object,         finalValues.object]        : prev.object,
        }));
        if (isFirst) setLocked({ character: true, sideCharacter: true, theme: true, object: false });
        setIsRolling(false);
        return;
      }
      // Cycle random words only for dice that will change
      setDisplay({
        character:     (!isFirst && locked.character)     ? rolled.character     : pick(DICE_POOL.character),
        sideCharacter: (!isFirst && locked.sideCharacter) ? rolled.sideCharacter : pick(DICE_POOL.sideCharacter),
        theme:         (!isFirst && locked.theme)         ? rolled.theme         : pick(DICE_POOL.theme),
        object:        (!isFirst && locked.object)        ? rolled.object        : pick(DICE_POOL.object),
      });
    }, 55);
  };

  const toggleLock = (key) => {
    if (!rolled || isRolling) return;
    setLocked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClear = () => {
    clearInterval(intervalRef.current);
    setRolled(null);
    setDisplay(null);
    setIsRolling(false);
    setHistory({ character: [], sideCharacter: [], theme: [], object: [] });
    setLocked({ character: false, sideCharacter: false, theme: false, object: false });
  };

  return (
    <main style={{ flex: 1, overflowY: 'auto', backgroundColor: theme.cardBg100, padding: 16 }}>
      <div style={{ backgroundColor: theme.cardBg, borderRadius: 20, padding: 20, display: 'flex', flexDirection: 'column', gap: 14, zoom: 0.9 }}>

        {/* Top row: dice grid (left) + history/roll (right) */}
        <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>

          {/* Dice grid */}
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 14 }}>
              <DiceCard label="Character"      value={display?.character}     locked={locked.character}     onToggleLock={() => toggleLock('character')}     hasRolled={!!rolled} />
              <DiceCard label="Side Character" value={display?.sideCharacter} locked={locked.sideCharacter} onToggleLock={() => toggleLock('sideCharacter')} hasRolled={!!rolled} />
            </div>
            <div style={{ display: 'flex', gap: 14 }}>
              <DiceCard label="Theme"  value={display?.theme}  locked={locked.theme}  onToggleLock={() => toggleLock('theme')}  hasRolled={!!rolled} />
              <DiceCard label="Object" value={display?.object} locked={locked.object} onToggleLock={() => toggleLock('object')} hasRolled={!!rolled} />
            </div>
          </div>

          {/* Right column: history + roll button */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* History panel */}
            <div style={{ backgroundColor: theme.cardBg100, borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 0 }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 10, borderBottom: `1px solid ${theme.border}`, marginBottom: 8 }}>
                <span className="font-satoshi font-bold" style={{ fontSize: 16, color: theme.text100, lineHeight: '28px' }}>History</span>
                <button
                  onClick={handleClear}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', opacity: history.character.length > 0 ? 1 : 0.35 }}
                  title="Clear history"
                >
                  <Trash size={20} variant="Linear" color={theme.text400} />
                </button>
              </div>

              {/* 4 fixed category rows */}
              {[
                { label: 'Character',      key: 'character' },
                { label: 'Side Character', key: 'sideCharacter' },
                { label: 'Theme',          key: 'theme' },
                { label: 'Object',         key: 'object' },
              ].map((row, i, arr) => (
                <div key={row.key} style={{ padding: '10px 0', borderBottom: i < arr.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                  <p className="font-satoshi font-bold" style={{ fontSize: 14, color: theme.text100, margin: 0, lineHeight: '22px' }}>{row.label}</p>
                  <p className="font-satoshi" style={{ fontSize: 13, color: theme.text400, margin: 0, lineHeight: '20px', minHeight: 20 }}>
                    {history[row.key].length === 0
                      ? '—'
                      : history[row.key].map((word, wi) => (
                          <span key={wi}>
                            <span style={{ color: wi === history[row.key].length - 1 ? theme.text100 : '#95acb6' }}>{word}</span>
                            {wi < history[row.key].length - 1 && <span style={{ color: theme.border }}> · </span>}
                          </span>
                        ))
                    }
                  </p>
                </div>
              ))}
            </div>

            {/* Roll The Dice */}
            <button
              onClick={handleRoll}
              className="font-satoshi font-bold"
              style={{
                width: '100%', height: 52,
                backgroundColor: theme.btnPrimaryBg, color: theme.btnPrimaryText,
                border: 'none', borderRadius: 16, fontSize: 14,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background-color 0.15s, transform 0.1s ease',
              }}
              onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.98)'; }}
              onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              Roll The Dice
            </button>
          </div>
        </div>

        {/* Bottom row: write story (left) + action buttons (right) */}
        <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', marginTop: 32 }}>

          {/* Textarea */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span className="font-satoshi font-bold" style={{ fontSize: 14, color: theme.text100, lineHeight: '22px' }}>
              Write your story here
            </span>
            <div style={{ position: 'relative', height: 116 }}>
              <textarea
                value={storyText}
                onChange={e => setStoryText(e.target.value)}
                placeholder="Start writing your story based on the dice roll..."
                className="font-satoshi"
                style={{
                  width: '100%', height: '100%', padding: '14px 16px',
                  boxSizing: 'border-box', resize: 'none', outline: 'none',
                  backgroundColor: theme.cardBg100, border: 'none', borderRadius: 16,
                  fontSize: 14, color: theme.text100, lineHeight: '22px',
                }}
              />
              <button
                style={{
                  position: 'absolute', bottom: 12, right: 12,
                  width: 40, height: 40, borderRadius: '50%',
                  backgroundColor: theme.cardBg, border: `1px solid ${theme.border}`,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background-color 0.15s',
                }}
                title="Voice input"
              >
                <Microphone2 size={18} variant="Linear" color={theme.text400} />
              </button>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ width: 248, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 32 }}>
            <button
              className="font-satoshi font-bold"
              style={{
                width: '100%', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                backgroundColor: theme.cardBg100, border: 'none', borderRadius: 16,
                fontSize: 14, color: theme.text200, cursor: 'pointer',
                transition: 'background-color 0.15s',
              }}
            >
              <Heart size={18} variant="Linear" color={theme.text200} />
              Mark as Favorite
            </button>
            <button
              className="font-satoshi font-bold"
              style={{
                width: '100%', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                backgroundColor: theme.cardBg100, border: 'none', borderRadius: 16,
                fontSize: 14, color: theme.text200, cursor: 'pointer',
                transition: 'background-color 0.15s',
              }}
            >
              <NoteText size={18} variant="Linear" color={theme.text200} />
              Publish Story
            </button>
          </div>
        </div>

      </div>
    </main>
  );
}

// --- Listen Mode Audio Screen (concentric rings player) ---

function ListenModeAudioScreen({ onBack, story, previewOrigin }) {
  const theme = useContext(ThemeContext);
  const [isPlaying, setIsPlaying] = React.useState(true);

  const isDrawTell = previewOrigin === 'draw-tell';
  const albumArt   = isDrawTell ? DRAW_TELL_LISTEN_ART_URL : (story?.image || LISTEN_ALBUM_ART_URL);
  const title      = isDrawTell ? "Johnny's 5th Birthday Gift" : (story?.title || 'Ryan the Forest Explorer');

  const RADIUS = 88;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const progress = 168 / 300; // 2:48 / 5:00
  const dashOffset = CIRCUMFERENCE * (1 - progress);

  return (
    <main style={{ flex: 1, backgroundColor: theme.cardBg, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>

      {/* Top nav row — absolutely positioned so it doesn't affect vertical centering */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', zIndex: 10 }}>
        <GlassBackButton onClick={onBack} size={36} />
        <GlassKebabButton size={36} />
      </div>

      {/* Audio player content — centered in full available space */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 22 }}>

        {/* Concentric rings + album art */}
        <div style={{ position: 'relative', width: 243, height: 243, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', width: 243, height: 243, borderRadius: '50%', backgroundColor: theme.audioRingOuter, boxShadow: '0 4px 29px rgba(0,0,0,0.06)' }} />
          <div style={{ position: 'absolute', width: 180, height: 180, borderRadius: '50%', backgroundColor: theme.border }} />
          <svg style={{ position: 'absolute', width: 158, height: 158, transform: 'rotate(-90deg)' }} viewBox="0 0 200 200">
            <circle cx="100" cy="100" r={RADIUS} fill="none" stroke={theme.border} strokeWidth="10" />
            <circle cx="100" cy="100" r={RADIUS} fill="none" stroke="#5d7b88" strokeWidth="10" strokeDasharray={CIRCUMFERENCE} strokeDashoffset={dashOffset} strokeLinecap="round" />
          </svg>
          <div style={{ position: 'relative', width: 137, height: 137, borderRadius: '50%', overflow: 'hidden', zIndex: 2 }}>
            <img src={albumArt} alt="Story cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        </div>

        {/* Title + time */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <p className="font-satoshi" style={{ fontSize: 20, fontWeight: 700, color: theme.text100, margin: 0, lineHeight: '29px' }}>{title}</p>
          <p className="font-satoshi" style={{ fontSize: 16, color: theme.text400, margin: 0, lineHeight: '22px' }}>2:48 / 5:00</p>
        </div>

        {/* Playback controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 47 }}>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, display: 'flex' }}>
            <Backward10Seconds size={36} variant="Bold" color={theme.modalCloseIcon} />
          </button>
          <button onClick={() => setIsPlaying(p => !p)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, display: 'flex' }}>
            {isPlaying
              ? <Pause size={42} variant="Bold" color={theme.modalCloseIcon} />
              : <Play  size={42} variant="Bold" color={theme.modalCloseIcon} />}
          </button>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, display: 'flex' }}>
            <Forward10Seconds size={36} variant="Bold" color={theme.modalCloseIcon} />
          </button>
        </div>

      </div>
    </main>
  );
}

// --- Watch Mode Screen (full-bleed video player) ---

function WatchModeScreen({ onBack, story, previewOrigin }) {
  const [isPlaying, setIsPlaying] = React.useState(true);
  const [showControls, setShowControls] = React.useState(false);
  const DURATION = 300; // 5:00
  const [currentTime, setCurrentTime] = React.useState(168); // 2:48
  const [isDragging, setIsDragging] = React.useState(false);
  const progressBarRef = React.useRef(null);

  const progress = currentTime / DURATION;

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const seekFromEvent = (e) => {
    const bar = progressBarRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setCurrentTime(Math.round(ratio * DURATION));
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
    seekFromEvent(e);
    const onMove = (ev) => seekFromEvent(ev);
    const onUp = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const controlsStyle = {
    opacity: showControls ? 1 : 0,
    pointerEvents: showControls ? 'auto' : 'none',
    transition: 'opacity 0.25s ease',
  };

  return (
    <main
      onClick={() => setShowControls(s => !s)}
      style={{ flex: 1, backgroundColor: '#262626', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
    >

      {/* Full-bleed background illustration */}
      <img src={previewOrigin === 'draw-tell' ? DRAW_TELL_WATCH_BG_URL : (story?.image || WATCH_BG_URL)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />

      {/* Bottom gradient overlay */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0) 45%, rgba(2,2,2,0.75) 100%)' }} />

      {/* Top nav — close + options */}
      <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', zIndex: 10, zoom: 0.9, ...controlsStyle }}>
        <GlassCloseButton onClick={onBack} size={36} />
        <GlassKebabButton size={36} />
      </div>

      {/* Bottom controls area */}
      <div style={{ position: 'absolute', bottom: 24, left: 0, right: 0, padding: '0 18px 18px', zIndex: 10, zoom: 0.9, ...controlsStyle }}>
        {/* Progress bar */}
        <div style={{ position: 'relative', height: 18, display: 'flex', alignItems: 'center', marginBottom: 11 }}>
          <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.3)' }} />
          <div style={{ position: 'absolute', left: 0, width: `${progress * 100}%`, height: 4, borderRadius: 2, backgroundColor: '#FFFFFF' }} />
          <div style={{ position: 'absolute', left: `${progress * 100}%`, transform: 'translateX(-50%)', width: 13, height: 13, borderRadius: '50%', backgroundColor: '#FFFFFF', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />
        </div>
        {/* Time labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 13 }}>
          <span className="font-satoshi" style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>2:48</span>
          <span className="font-satoshi" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>5:00</span>
        </div>
      </div>

      {/* Playback controls — centred on screen */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 59, zIndex: 10, pointerEvents: showControls ? 'auto' : 'none', opacity: showControls ? 1 : 0, transition: 'opacity 0.25s ease' }}>
        <button onClick={e => { e.stopPropagation(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', opacity: 0.7 }}>
          <Backward10Seconds size={36} variant="Bold" color="#ffffff" />
        </button>
        <button onClick={e => { e.stopPropagation(); setIsPlaying(p => !p); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', opacity: 0.7 }}>
          {isPlaying
            ? <Pause size={42} variant="Bold" color="#ffffff" />
            : <Play  size={42} variant="Bold" color="#ffffff" />}
        </button>
        <button onClick={e => { e.stopPropagation(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', opacity: 0.7 }}>
          <Forward10Seconds size={36} variant="Bold" color="#ffffff" />
        </button>
      </div>


    </main>
  );
}

// --- Freeform Story Mode: Read / Listen / Watch ---
// Copies of the personalised screens. Only difference: default category label reads "Freeform Story Mode".

function FreeformStoryReadScreen({ onBack, story, onListen, onWatch }) {
  const theme = useContext(ThemeContext);
  const sc = (n) => Math.round(n * 0.63);

  const heroImage = story?.image || STORY_READ_HERO_URL;
  const title     = story?.title    || 'Ryan the Forest Explorer';
  const category  = story?.category || 'Freeform Story Mode';
  const ageGroup  = story?.ageGroup || '3 to 5 year old';

  const bodyStyle = { fontFamily: "'Satoshi', sans-serif", fontWeight: 400, fontSize: sc(20), lineHeight: `${sc(32)}px`, color: theme.text100, margin: 0 };

  return (
    <main style={{ flex: 1, overflowY: 'auto', backgroundColor: theme.cardBg, display: 'flex', flexDirection: 'column' }}>

      {/* ── Hero image ── */}
      <div style={{ position: 'relative', margin: `${sc(56)}px ${sc(58)}px 0`, borderRadius: sc(40), overflow: 'hidden', flexShrink: 0, height: sc(400) }}>
        <img src={heroImage} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 35%', display: 'block' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(78,65,54,0) 40%, rgba(0,0,0,0.55) 100%)' }} />

        {/* Title + category */}
        <div style={{ position: 'absolute', bottom: sc(40), left: sc(40), right: sc(80) }}>
          <h2 className="font-satoshi" style={{ color: '#FFFFFF', fontSize: sc(40), fontWeight: 900, margin: 0, letterSpacing: '0.4px', lineHeight: 1.4 }}>{title}</h2>
          <p  className="font-satoshi" style={{ color: '#D9D9D9', fontSize: sc(18), fontWeight: 400, margin: `${sc(4)}px 0 0`, letterSpacing: '0.18px', lineHeight: 1.6 }}>{category} | Age Group: {ageGroup}</p>
        </div>

        {/* Back button */}
        <button onClick={onBack} style={{ position: 'absolute', top: sc(32), left: sc(32), width: sc(48), height: sc(48), borderRadius: sc(24), backgroundColor: 'rgba(0,0,0,0.3)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)', flexShrink: 0 }}>
          <ArrowLeft2 size={sc(24)} color="#FFFFFF" variant="Linear" />
        </button>

        {/* Kebab button */}
        <button style={{ position: 'absolute', top: sc(32), right: sc(32), width: sc(48), height: sc(48), borderRadius: sc(24), backgroundColor: 'rgba(0,0,0,0.3)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)', flexShrink: 0 }}>
          <svg width={sc(24)} height={sc(24)} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="5" r="1.8" fill="#FFFFFF"/><circle cx="12" cy="12" r="1.8" fill="#FFFFFF"/><circle cx="12" cy="19" r="1.8" fill="#FFFFFF"/></svg>
        </button>
      </div>

      {/* ── Story body ── */}
      <div style={{ padding: `${sc(48)}px ${sc(58)}px 60px`, display: 'flex', flexDirection: 'column', gap: sc(64) }}>

        {/* Section 1: opening paragraph */}
        <p style={bodyStyle}>Ryan, a curious 6-year-old boy, loved exploring the forest behind his house. He wore a wide-brimmed hat, carried a small backpack, and proudly called himself the Forest Explorer. Every morning after breakfast, he grabbed his journal and walked into the woods with a big smile on his face. One sunny day, Ryan discovered a new path filled with tall trees, colorful leaves, and birdsong in the air. As he followed the trail, he heard a soft hopping sound behind him. It was Rabbit, his cheerful friend with floppy ears and a twitchy nose.</p>

        {/* Section 2: text left + image right */}
        <div style={{ display: 'flex', gap: sc(48), alignItems: 'stretch' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={bodyStyle}>"Ready for a new adventure?" Rabbit asked.<br/>"Always," Ryan said with a grin.<br/><br/>They wandered deeper into the forest. Along the way, Ryan spotted something near a tree stump. It was a perfectly round, clean football. He picked it up and bounced it on the ground.<br/>"Let's play," he said.<br/><br/>Ryan and Rabbit kicked the football between the trees. They ran, laughed, and tried tricky moves under the tall canopy of leaves. After a while, they sat down to rest.<br/><br/>Ryan pulled a book out of his backpack. "Time for some reading," he said.</p>
          </div>
          <div style={{ flexShrink: 0, width: '43%', borderRadius: sc(40), overflow: 'hidden', alignSelf: 'stretch', minHeight: sc(280) }}>
            <img src={READ_INLINE_IMG_1} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        </div>

        {/* Section 3: full-width continuation */}
        <p style={bodyStyle}>Rabbit leaned closer. Ryan read a story about a clever bird who solved puzzles to protect its nest. As he read aloud, a quiet hoot echoed from the branches above. An owl perched on a high limb, watching them silently. Ryan noticed it and smiled but didn't know its name or why it followed him.</p>

        {/* Section 4: image left + text right */}
        <div style={{ display: 'flex', gap: sc(48), alignItems: 'stretch' }}>
          <div style={{ flexShrink: 0, width: '55%', borderRadius: sc(40), overflow: 'hidden', alignSelf: 'stretch', minHeight: sc(280) }}>
            <img src={READ_INLINE_IMG_2} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={bodyStyle}>Later, Ryan pulled out his tiny camp stove and a lunchbox. "Let's do some cooking," he said.<br/>He and Rabbit gathered a few forest berries and mixed them with nuts. Using a small pan, they made a warm berry snack. The sweet smell drifted through the trees.<br/><br/>Suddenly, the bushes rustled. Out stepped a large tiger, golden-eyed with bold black stripes. He sniffed the air.<br/>"I smell something tasty," the tiger said, stepping closer.<br/><br/>Ryan stood up slowly and stayed calm. He reached into his backpack and took out his sketchbook.<br/>"Would you like to see some art?" he asked.<br/><br/>The tiger paused. Ryan flipped the pages and showed him a drawing. It was a picture of a tiger standing by a river, drawn in bright colors.</p>
          </div>
        </div>

        {/* Section 5: closing paragraph */}
        <p style={bodyStyle}>"That's me," the tiger said, his voice softer now.<br/>"Yes. I saw your paw prints here last week," Ryan replied.<br/>The tiger looked at the drawing again, then nodded slowly. Without another word, he turned and walked back into the trees.<br/><br/>Rabbit looked at Ryan. "That was close."<br/>Ryan smiled. "Sometimes art speaks louder than words."<br/>As the sun dipped lower, Ryan packed his things. He waved at the owl, gave Rabbit a high five, and started walking home.<br/><br/>His journal now held notes about football, reading, cooking, and art. But more than that, it held the memories of a day spent being brave, kind, and curious. Ryan, the Forest Explorer, couldn't wait to return the next day.</p>

        {/* Moral of the Story */}
        <div style={{ backgroundColor: theme.moralBoxBg, borderRadius: sc(20), padding: sc(24), display: 'flex', flexDirection: 'column', gap: sc(5) }}>
          <p className="font-satoshi font-bold" style={{ fontSize: sc(24), lineHeight: `${sc(32)}px`, color: theme.text200, margin: 0 }}>Moral of the Story</p>
          <p className="font-satoshi"           style={{ fontSize: sc(20), lineHeight: `${sc(32)}px`, color: theme.text200, margin: 0, fontWeight: 400 }}>Curiosity and kindness make every adventure special. When one stays calm and uses their heart, even scary moments can turn into beautiful learning experiences.</p>
        </div>

        {/* Listen + Watch buttons */}
        <div style={{ display: 'flex', gap: sc(24), justifyContent: 'center' }}>
          <Button variant="secondary" scale={0.75} onClick={onListen} style={{ flex: 1, maxWidth: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: sc(12) }}>
            <HeadphoneIcon color={theme.text200} />
            Listen to this story
          </Button>
          <Button variant="secondary" scale={0.75} onClick={onWatch} style={{ flex: 1, maxWidth: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: sc(12) }}>
            <VideoPlayIcon color={theme.text200} />
            Watch this story
          </Button>
        </div>

      </div>
    </main>
  );
}

function FreeformListenModeScreen({ onBack, story }) {
  const theme = useContext(ThemeContext);
  const [isPlaying, setIsPlaying] = React.useState(true);

  const RADIUS = 88;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const progress = 168 / 300;
  const dashOffset = CIRCUMFERENCE * (1 - progress);

  return (
    <main style={{ flex: 1, backgroundColor: theme.cardBg, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>

      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', zIndex: 10 }}>
        <GlassBackButton onClick={onBack} size={36} />
        <GlassKebabButton size={36} />
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 22 }}>

        <div style={{ position: 'relative', width: 243, height: 243, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', width: 243, height: 243, borderRadius: '50%', backgroundColor: theme.audioRingOuter, boxShadow: '0 4px 29px rgba(0,0,0,0.06)' }} />
          <div style={{ position: 'absolute', width: 180, height: 180, borderRadius: '50%', backgroundColor: theme.border }} />
          <svg style={{ position: 'absolute', width: 158, height: 158, transform: 'rotate(-90deg)' }} viewBox="0 0 200 200">
            <circle cx="100" cy="100" r={RADIUS} fill="none" stroke={theme.border} strokeWidth="10" />
            <circle cx="100" cy="100" r={RADIUS} fill="none" stroke="#5d7b88" strokeWidth="10" strokeDasharray={CIRCUMFERENCE} strokeDashoffset={dashOffset} strokeLinecap="round" />
          </svg>
          <div style={{ position: 'relative', width: 137, height: 137, borderRadius: '50%', overflow: 'hidden', zIndex: 2 }}>
            <img src={story?.image || LISTEN_ALBUM_ART_URL} alt="Story cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        </div>

        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <p className="font-satoshi" style={{ fontSize: 20, fontWeight: 700, color: theme.text100, margin: 0, lineHeight: '29px' }}>{story?.title || 'Ryan the Forest Explorer'}</p>
          <p className="font-satoshi" style={{ fontSize: 16, color: theme.text400, margin: 0, lineHeight: '22px' }}>2:48 / 5:00</p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 47 }}>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, display: 'flex' }}>
            <img src={LISTEN_BACKWARD_URL} alt="Backward 10s" style={{ width: 36, height: 36 }} />
          </button>
          <button onClick={() => setIsPlaying(p => !p)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, display: 'flex' }}>
            <img src={LISTEN_PAUSE_URL} alt={isPlaying ? 'Pause' : 'Play'} style={{ width: 40, height: 40 }} />
          </button>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, display: 'flex' }}>
            <img src={LISTEN_FORWARD_URL} alt="Forward 10s" style={{ width: 36, height: 36 }} />
          </button>
        </div>

      </div>
    </main>
  );
}

function FreeformWatchModeScreen({ onBack, story }) {
  const [isPlaying, setIsPlaying] = React.useState(true);
  const [showControls, setShowControls] = React.useState(false);
  const DURATION = 300;
  const [currentTime, setCurrentTime] = React.useState(168);
  const [isDragging, setIsDragging] = React.useState(false);
  const progressBarRef = React.useRef(null);

  const progress = currentTime / DURATION;

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const seekFromEvent = (e) => {
    const bar = progressBarRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setCurrentTime(Math.round(ratio * DURATION));
  };

  const handleMouseDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
    seekFromEvent(e);
    const onMove = (ev) => seekFromEvent(ev);
    const onUp = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const controlsStyle = {
    opacity: showControls ? 1 : 0,
    pointerEvents: showControls ? 'auto' : 'none',
    transition: 'opacity 0.25s ease',
  };

  return (
    <main
      onClick={() => setShowControls(s => !s)}
      style={{ flex: 1, backgroundColor: '#262626', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
    >
      <img src={story?.image || WATCH_BG_URL} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0) 45%, rgba(2,2,2,0.75) 100%)' }} />

      <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', zIndex: 10, zoom: 0.9, ...controlsStyle }}>
        <GlassCloseButton onClick={onBack} size={36} />
        <GlassKebabButton size={36} />
      </div>

      <div style={{ position: 'absolute', bottom: 24, left: 0, right: 0, padding: '0 18px 18px', zIndex: 10, zoom: 0.9, ...controlsStyle }}>
        <div style={{ position: 'relative', height: 18, display: 'flex', alignItems: 'center', marginBottom: 11 }}>
          <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.3)' }} />
          <div style={{ position: 'absolute', left: 0, width: `${progress * 100}%`, height: 4, borderRadius: 2, backgroundColor: '#FFFFFF' }} />
          <div style={{ position: 'absolute', left: `${progress * 100}%`, transform: 'translateX(-50%)', width: 13, height: 13, borderRadius: '50%', backgroundColor: '#FFFFFF', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 13 }}>
          <span className="font-satoshi" style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)' }}>{formatTime(currentTime)}</span>
          <span className="font-satoshi" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{formatTime(DURATION)}</span>
        </div>
      </div>

      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 59, zIndex: 10, pointerEvents: showControls ? 'auto' : 'none', opacity: showControls ? 1 : 0, transition: 'opacity 0.25s ease' }}>
        <button onClick={e => { e.stopPropagation(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
          <img src={WATCH_BACKWARD_URL} alt="Backward 10s" style={{ width: 36, height: 36, filter: 'brightness(0) invert(1)', opacity: 0.7 }} />
        </button>
        <button onClick={e => { e.stopPropagation(); setIsPlaying(p => !p); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
          <img src={WATCH_PAUSE_URL} alt={isPlaying ? 'Pause' : 'Play'} style={{ width: 40, height: 40, filter: 'brightness(0) invert(1)', opacity: 0.7 }} />
        </button>
        <button onClick={e => { e.stopPropagation(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
          <img src={WATCH_FORWARD_URL} alt="Forward 10s" style={{ width: 36, height: 36, filter: 'brightness(0) invert(1)', opacity: 0.7 }} />
        </button>
      </div>
    </main>
  );
}

// --- Draw & Tell Mode ---

// Tool icons
const DrawPencilToolIcon = ({ color = '#404040', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M17 3a2.85 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DrawBrushToolIcon = ({ color = '#404040', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M9.06 11.9l8.07-8.06a2.85 2.83 0 114 4l-8.06 8.07" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.07 14.94C5.73 16.28 4 16.5 4 16.5s.22-1.77 1.56-3.12A3 3 0 119.12 17.5c-.37-.37-.07-1.06.28-1.75" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DrawPenToolIcon = ({ color = '#404040', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 19l7-7 3 3-7 7-3-3z" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2 2l7.59 7.59" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="11" cy="11" r="2" stroke={color} strokeWidth="1.5"/>
  </svg>
);

const DrawEraserToolIcon = ({ color = '#404040', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M20 20H7L3 16l10-10 7 7-4.5 4.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6.5 17.5l4-4" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const DrawUndoToolIcon = ({ color = '#404040', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 0 1 0 12H9" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DrawStrokeToolIcon = ({ color = '#404040', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M4 7h16" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M4 12h16" stroke={color} strokeWidth="3" strokeLinecap="round"/>
    <path d="M4 18h16" stroke={color} strokeWidth="5" strokeLinecap="round"/>
  </svg>
);

const DrawRulerToolIcon = ({ color = '#404040', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="8.5" width="18" height="7" rx="1.5" stroke={color} strokeWidth="1.8"/>
    <path d="M7 8.5v2.5M10.5 8.5v4M14 8.5v2.5M17 8.5v4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const TagElementsIcon = ({ color = '#404040' }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <path d="M12.59 2.59a2 2 0 00-1.42-.59H4a2 2 0 00-2 2v7.17a2 2 0 00.59 1.42l8 8a2 2 0 002.82 0l7.17-7.17a2 2 0 000-2.83l-8-8z" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="7.5" cy="7.5" r="1.5" fill={color}/>
  </svg>
);

const UploadImgIcon = ({ color = '#FFFFFF' }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="1.8"/>
    <circle cx="8.5" cy="8.5" r="1.5" stroke={color} strokeWidth="1.5"/>
    <path d="M3 16l5-5 3.5 3.5 3-3 6.5 5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ExpandCanvasIcon = ({ color = '#737373' }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Regions are % of canvas (1400×600). Image is 2496×1656; cover crops 293px from each side vertically.
// canvas_x% = img_px/2496*100  |  canvas_y% = (img_px−293)/1070*100
const TAG_ELEMENTS = [
  { id: 'happy-birthday', label: 'Happy Birthday Text', border: '#d46c6c', fill: 'rgba(212,108,108,0.20)',
    regions: [{ x: 17, y: 0,  w: 66, h: 40 }] },
  { id: 'boy',            label: 'Boy',                 border: '#5d7b88', fill: 'rgba(93,123,136,0.25)',
    regions: [{ x: 10,  y: 23, w: 22, h: 77 }] },
  { id: 'balloons',       label: 'Balloons',            border: '#f4c8a3', fill: 'rgba(244,200,163,0.35)',
    regions: [{ x: 40, y: 36, w: 24, h: 39 }, { x: 72, y: 28, w: 25, h: 40 }] },
  { id: 'cake',           label: 'Cake',                border: '#76a672', fill: 'rgba(118,166,114,0.30)',
    regions: [{ x: 57, y: 52, w: 20, h: 48 }] },
  { id: 'gifts',          label: 'Gifts',               border: '#dbaa6e', fill: 'rgba(219,170,110,0.35)',
    regions: [{ x: 34, y: 85, w: 17, h: 15 }, { x: 78, y: 82, w: 12, h: 18 }] },
];

function DrawingBoard({ isFullscreen = false, onToggleFullscreen }) {
  const theme = useContext(ThemeContext);
  const canvasRef = React.useRef(null);
  const boardRef = React.useRef(null);
  const colorInputRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const lastPosRef = React.useRef(null);
  const snapshotRef = React.useRef(null);
  const isDraggingToolbarRef = React.useRef(false);
  const dragOffsetRef = React.useRef({ x: 0, y: 0 });

  const greyContainerRef = React.useRef(null);
  const fsWrapperRef = React.useRef(null);

  const [activeTool, setActiveTool] = React.useState('pencil');
  const [strokeColor, setStrokeColor] = React.useState('#262626');
  const [strokeSize, setStrokeSize] = React.useState(3);
  const isDrawingRef = React.useRef(false);
  const [history, setHistory] = React.useState([]);
  const [showSizePanel, setShowSizePanel] = React.useState(false);
  const [toolbarPos, setToolbarPos] = React.useState({ x: 16, y: 16 });
  const [fsToolbarPos, setFsToolbarPos] = React.useState({ x: 0, y: 0 });
  const [fsExiting, setFsExiting] = React.useState(false);
  const [fsRect, setFsRect] = React.useState(null);
  const [showClearModal,    setShowClearModal]    = React.useState(false);
  const [isClearClosing,    setIsClearClosing]    = React.useState(false);
  const [tagPanelOpen,      setTagPanelOpen]      = React.useState(false);
  const [activeTag,         setActiveTag]         = React.useState(null);
  const [showEmptyToast,    setShowEmptyToast]    = React.useState(false);
  const [canvasCleared,     setCanvasCleared]     = React.useState(false);

  const handleToggleFullscreen = () => {
    if (isFullscreen) {
      setFsExiting(true);
      setTimeout(() => { setFsExiting(false); onToggleFullscreen(); }, 500);
    } else {
      const appEl = boardRef.current?.closest('[data-app-root]');
      if (appEl) {
        const r = appEl.getBoundingClientRect();
        setFsRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
      onToggleFullscreen();
    }
  };

  const CANVAS_W = 1400;
  const CANVAS_H = 600;
  const TOOLBAR_W = 52;
  const TOOLBAR_H = 310;
  const FS_TOOLBAR_W = 306; // horizontal toolbar: 7 × 38px + padding + drag handle
  const FS_TOOLBAR_H = 50;

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  // Drag logic — attach to window so drag continues outside toolbar
  React.useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDraggingToolbarRef.current) return;
      if (isFullscreen) {
        const wrapper = fsWrapperRef.current;
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();
        const x = Math.max(0, Math.min(e.clientX - rect.left - dragOffsetRef.current.x, rect.width - FS_TOOLBAR_W));
        const y = Math.max(0, Math.min(e.clientY - rect.top  - dragOffsetRef.current.y, rect.height - FS_TOOLBAR_H));
        setFsToolbarPos({ x, y });
      } else {
        const board = boardRef.current;
        if (!board) return;
        const rect = board.getBoundingClientRect();
        const x = Math.max(0, Math.min(e.clientX - rect.left - dragOffsetRef.current.x, rect.width - TOOLBAR_W));
        const y = Math.max(0, Math.min(e.clientY - rect.top  - dragOffsetRef.current.y, rect.height - TOOLBAR_H));
        setToolbarPos({ x, y });
      }
    };
    const onMouseUp = () => { isDraggingToolbarRef.current = false; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isFullscreen]);


  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - rect.left) * sx, y: (cy - rect.top) * sy };
  };

  const saveSnapshot = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      const snapshot = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
      setHistory(prev => [...prev.slice(-19), snapshot]);
      setCanvasCleared(false);
    } catch (_) {}
  };

  const applyStyle = (ctx) => {
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalAlpha = 1;
    if (activeTool === 'eraser') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = strokeSize * 6;
    } else if (activeTool === 'brush') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeSize * 2.5;
      ctx.globalAlpha = 0.6;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = activeTool === 'pen' ? Math.max(1, strokeSize * 0.6) : strokeSize;
    }
  };

  const onCanvasMouseDown = (e) => {
    isDraggingToolbarRef.current = false;
    e.preventDefault();
    isDrawingRef.current = true;
    lastPosRef.current = getPos(e);
    saveSnapshot();
    if (activeTool === 'line') {
      try {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        snapshotRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
      } catch (_) {}
    }
  };

  const onCanvasMouseMove = (e) => {
    if (isDraggingToolbarRef.current || !isDrawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);

    if (activeTool === 'line') {
      if (!snapshotRef.current) return;
      ctx.putImageData(snapshotRef.current, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeSize;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else {
      applyStyle(ctx);
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      lastPosRef.current = pos;
    }
  };

  const onCanvasMouseUp = (e) => {
    if (!isDrawingRef.current) return;
    if (activeTool === 'line' && e && snapshotRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const pos = getPos(e);
      ctx.putImageData(snapshotRef.current, 0, 0);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeSize;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      snapshotRef.current = null;
    }
    isDrawingRef.current = false;
    lastPosRef.current = null;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
    }
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(history[history.length - 1], 0, 0);
    setHistory(prev => prev.slice(0, -1));
  };

  const handleClear = () => { setShowClearModal(true); setIsClearClosing(false); };

  const canvasHasContent = !canvasCleared;

  const handleTagElementsClick = () => {
    if (!canvasHasContent) {
      setShowEmptyToast(true);
      setTimeout(() => setShowEmptyToast(false), 3000);
      return;
    }
    setTagPanelOpen(p => {
      if (p) setActiveTag(null);
      return !p;
    });
  };

  const closeClearModal = () => {
    setIsClearClosing(true);
    setTimeout(() => { setShowClearModal(false); setIsClearClosing(false); }, 760);
  };

  const confirmClear = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = CANVAS_W;
      canvas.height = CANVAS_H;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    }
    setHistory([]);
    setCanvasCleared(true);
    setTagPanelOpen(false);
    setActiveTag(null);
    closeClearModal();
  };

  const handleUploadImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        saveSnapshot();
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height, 1);
        const w = img.width * scale;
        const h = img.height * scale;
        const x = (canvas.width - w) / 2;
        const y = (canvas.height - h) / 2;
        ctx.drawImage(img, x, y, w, h);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const TOOLS = [
    { id: 'color',   Icon: () => (
        <div style={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: strokeColor, border: '2px solid #E6E6E6', flexShrink: 0 }} />
      ), label: 'Color', isColorPicker: true },
    { id: 'pencil',  Icon: DrawPencilToolIcon,  label: 'Pencil' },
    { id: 'eraser',  Icon: ({ color }) => <Eraser size={21} variant="Linear" color={color} />,  label: 'Eraser' },
    { id: 'undo',    Icon: DrawUndoToolIcon,    label: 'Undo',  isAction: true },
    { id: 'brush',   Icon: ({ color }) => <Brush size={21} variant="Linear" color={color} />,   label: 'Brush' },
    { id: 'line',    Icon: DrawRulerToolIcon,   label: 'Ruler/Line' },
    { id: 'clear',   Icon: ({ color }) => <Trash size={21} variant="Linear" color={color} />, label: 'Clear All', isAction: true },
  ];

  const SIZE_OPTIONS = [2, 4, 8, 14];
  const canvasCursor = activeTool === 'eraser' ? 'cell' : 'crosshair';

  return (
    <div ref={isFullscreen ? greyContainerRef : undefined} style={isFullscreen ? {
      position: 'fixed',
      top: fsRect?.top ?? 0,
      left: fsRect?.left ?? 0,
      width: fsRect?.width ?? 872,
      height: fsRect?.height ?? '90vh',
      borderRadius: 16,
      overflow: 'hidden',
      backgroundColor: theme.cardBg100, zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    } : { display: 'contents' }}>
      <style>{`
        @keyframes fsContentIn {
          from { opacity: 0; transform: scale(0.92); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes fsContentOut {
          from { opacity: 1; transform: scale(1); }
          to   { opacity: 0; transform: scale(0.92); }
        }
        @keyframes tagHighlightPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.45; }
        }
        @keyframes tagToastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(8px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

    {/* Canvas wrapper — toolbar + action buttons row + canvas in fullscreen */}
    <div ref={isFullscreen ? fsWrapperRef : undefined} style={isFullscreen ? {
      display: 'flex', flexDirection: 'column', gap: 8,
      width: 'calc(100% - 24px)',
      position: 'relative',
      animation: fsExiting
        ? 'fsContentOut 0.5s ease forwards'
        : 'fsContentIn 0.5s ease forwards',
    } : { display: 'contents' }}>

      {isFullscreen && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          {/* Action buttons — inline right */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="font-satoshi"
              onClick={handleTagElementsClick}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px',
                backgroundColor: tagPanelOpen ? theme.btnPrimaryBg : theme.cardBg,
                border: tagPanelOpen ? 'none' : `1px solid ${theme.border}`, borderRadius: 10,
                fontSize: 13, color: tagPanelOpen ? theme.btnPrimaryText : theme.text200, cursor: 'pointer',
                fontWeight: 500, transition: 'all 0.15s', flexShrink: 0,
              }}
            >
              <TagElementsIcon color={tagPanelOpen ? theme.btnPrimaryText : theme.text200} /> Tag Elements
            </button>
            <button className="font-satoshi"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', backgroundColor: theme.btnPrimaryBg,
                border: 'none', borderRadius: 10,
                fontSize: 13, color: theme.btnPrimaryText, cursor: 'pointer',
                fontWeight: 500, transition: 'background-color 0.12s', flexShrink: 0,
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadImgIcon color={theme.btnPrimaryText} /> Upload image
            </button>
          </div>
        </div>
      )}

    <div
      ref={boardRef}
      style={isFullscreen ? {
        width: '100%',
        aspectRatio: '788 / 460',
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
        backgroundImage: `url(${DRAW_TELL_CANVAS_BG_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      } : {
        backgroundColor: '#FFFFFF', borderRadius: 20,
        margin: '12px 12px 0', height: 460,
        flexShrink: 0, overflow: 'hidden',
        position: 'relative',
        backgroundImage: `url(${DRAW_TELL_CANVAS_BG_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* ── Full-size canvas ── */}
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        style={{ width: '100%', height: '100%', display: 'block', cursor: canvasCursor }}
        onMouseDown={onCanvasMouseDown}
        onMouseMove={onCanvasMouseMove}
        onMouseUp={onCanvasMouseUp}
        onMouseLeave={onCanvasMouseUp}
      />

      {/* Hidden inputs */}
      <input ref={colorInputRef} type="color" value={strokeColor} onChange={e => setStrokeColor(e.target.value)}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }} />
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUploadImage}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }} />

      {/* ── Floating draggable toolbar (normal mode only) ── */}
      {!isFullscreen && <div
        style={{
          position: 'absolute',
          left: toolbarPos.x,
          top: toolbarPos.y,
          zIndex: 10,
          width: TOOLBAR_W,
          border: '1.5px solid #D9D9D9',
          borderRadius: 16,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(6px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          userSelect: 'none',
        }}
      >
        {/* Drag handle */}
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            isDraggingToolbarRef.current = true;
            const toolbarEl = e.currentTarget.parentElement;
            const rect = toolbarEl.getBoundingClientRect();
            dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
          }}
          style={{
            width: '100%',
            padding: '6px 0 4px',
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '14px 14px 0 0',
          }}
        >
          {/* Grip dots */}
          <svg width="18" height="8" viewBox="0 0 18 8" fill="none">
            {[2,7,12,17].map(cx => [2,6].map(cy => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.2" fill="#E6E6E6" />
            )))}
          </svg>
        </div>

        {/* Tool buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2px 6px 8px', gap: 2 }}>
          {TOOLS.map(({ id, Icon, label, isColorPicker, isAction }) => {
            const active = !isAction && !isColorPicker && activeTool === id;
            return (
              <button
                key={id}
                title={label}
                onClick={() => {
                  if (isColorPicker) colorInputRef.current?.click();
                  else if (isAction) { if (id === 'clear') handleClear(); else handleUndo(); }
                  else if (id === 'brush') { setShowSizePanel(p => !p); setActiveTool('brush'); }
                  else { setActiveTool(id); setShowSizePanel(false); }
                }}
                style={{
                  width: 38, height: 38, border: 'none', borderRadius: 10,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: active ? '#f0f3f5' : 'transparent',
                  flexShrink: 0, transition: 'background-color 0.12s',
                }}
              >
                {typeof Icon === 'function' && id !== 'color'
                  ? <Icon color={active ? '#495f6a' : '#404040'} size={21} />
                  : <Icon />}
              </button>
            );
          })}
        </div>
      </div>}

      {/* Stroke size panel — follows toolbar (normal mode only) */}
      {!isFullscreen && showSizePanel && (
        <div style={{
          position: 'absolute',
          left: toolbarPos.x + TOOLBAR_W + 6,
          top: toolbarPos.y + 100,
          zIndex: 20,
          backgroundColor: '#FFFFFF', border: '1.5px solid #D9D9D9',
          borderRadius: 12, padding: '10px 8px',
          display: 'flex', flexDirection: 'column', gap: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
        }}>
          {SIZE_OPTIONS.map(sz => (
            <button
              key={sz}
              onClick={() => { setStrokeSize(sz); setShowSizePanel(false); }}
              style={{
                width: 80, height: 30, border: 'none', borderRadius: 8, cursor: 'pointer',
                backgroundColor: strokeSize === sz ? '#f0f3f5' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background-color 0.1s',
              }}
            >
              <div style={{ width: 60, height: sz * 1.2, backgroundColor: '#404040', borderRadius: sz / 2 + 1 }} />
            </button>
          ))}
        </div>
      )}

      {/* Top-right action buttons (normal mode only) */}
      {!isFullscreen && <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 8, zIndex: 15 }}>
        <button
          className="font-satoshi"
          onClick={handleTagElementsClick}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px',
            backgroundColor: tagPanelOpen ? '#303f46' : 'rgba(255,255,255,0.9)',
            border: tagPanelOpen ? 'none' : '1px solid #E6E6E6', borderRadius: 10,
            fontSize: 13, color: tagPanelOpen ? '#ffffff' : '#404040', cursor: 'pointer',
            fontWeight: 500, transition: 'all 0.15s', flexShrink: 0,
            backdropFilter: 'blur(4px)',
          }}
        >
          <TagElementsIcon color={tagPanelOpen ? '#ffffff' : '#404040'} />
          Tag Elements
        </button>
        <button
          className="font-satoshi"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', backgroundColor: '#303F46',
            border: 'none', borderRadius: 10,
            fontSize: 13, color: '#FFFFFF', cursor: 'pointer',
            fontWeight: 500, transition: 'background-color 0.12s', flexShrink: 0,
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadImgIcon color="#FFFFFF" />
          Upload image
        </button>
      </div>}

      {/* Tag element highlight overlays */}
      {tagPanelOpen && activeTag && (() => {
        const el = TAG_ELEMENTS.find(e => e.id === activeTag);
        return el?.regions.map((r, i) => (
          <div key={i} style={{
            position: 'absolute',
            left: `${r.x}%`, top: `${r.y}%`,
            width: `${r.w}%`, height: `${r.h}%`,
            border: `2.5px solid ${el.border}`,
            backgroundColor: el.fill,
            borderRadius: 10,
            pointerEvents: 'none',
            zIndex: 8,
            animation: 'tagHighlightPulse 1.8s ease-in-out infinite',
          }} />
        ));
      })()}

      {/* Tag chip panel — normal mode */}
      {!isFullscreen && tagPanelOpen && (
        <div style={{
          position: 'absolute', top: 50, right: 10, zIndex: 20,
          backgroundColor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(8px)',
          border: '1px solid #e6e6e6',
          borderRadius: 12, padding: '8px 10px',
          display: 'flex', gap: 6, flexWrap: 'wrap',
          maxWidth: 320,
          boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
        }}>
          {TAG_ELEMENTS.map(el => (
            <button key={el.id}
              className="font-satoshi"
              onClick={() => setActiveTag(id => id === el.id ? null : el.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 11px', borderRadius: 20,
                border: `1.5px solid ${activeTag === el.id ? el.border : '#e6e6e6'}`,
                backgroundColor: activeTag === el.id ? el.fill : 'transparent',
                cursor: 'pointer', fontSize: 12, fontWeight: 500,
                color: '#404040', transition: 'all 0.15s',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: el.border, flexShrink: 0 }} />
              {el.label}
            </button>
          ))}
        </div>
      )}

      {/* Tag chip panel — fullscreen mode */}
      {isFullscreen && tagPanelOpen && (
        <div style={{
          position: 'absolute', top: 10, right: 10, zIndex: 20,
          backgroundColor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(8px)',
          border: '1px solid #e6e6e6',
          borderRadius: 12, padding: '8px 10px',
          display: 'flex', gap: 6, flexWrap: 'wrap',
          maxWidth: 340,
          boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
        }}>
          {TAG_ELEMENTS.map(el => (
            <button key={el.id}
              className="font-satoshi"
              onClick={() => setActiveTag(id => id === el.id ? null : el.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 11px', borderRadius: 20,
                border: `1.5px solid ${activeTag === el.id ? el.border : '#e6e6e6'}`,
                backgroundColor: activeTag === el.id ? el.fill : 'transparent',
                cursor: 'pointer', fontSize: 12, fontWeight: 500,
                color: '#404040', transition: 'all 0.15s',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: el.border, flexShrink: 0 }} />
              {el.label}
            </button>
          ))}
        </div>
      )}

      {/* Empty canvas tooltip — anchored near top-right Tag Elements button */}
      {showEmptyToast && (
        <div className="font-satoshi" style={{
          position: 'absolute', top: 50, right: 10,
          backgroundColor: '#303f46', color: '#ffffff',
          fontSize: 12, fontWeight: 500, lineHeight: '18px',
          padding: '8px 14px', borderRadius: 10,
          zIndex: 30, maxWidth: 220, textAlign: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          animation: 'tagToastIn 0.2s ease forwards',
        }}>
          <div style={{
            position: 'absolute', bottom: '100%', right: 60,
            border: '6px solid transparent', borderBottomColor: '#303f46',
          }} />
          No elements exist on canvas for tagging
        </div>
      )}

      {/* Bottom-right expand / collapse button */}
      <button
        style={{
          position: 'absolute', bottom: 8, right: 8,
          width: 28, height: 28, borderRadius: '50%',
          backgroundColor: 'rgba(217,217,217,0.85)', border: 'none',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background-color 0.12s',
        }}
        onClick={handleToggleFullscreen}
        title={isFullscreen ? 'Collapse canvas' : 'Expand canvas'}
      >
        {isFullscreen ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M9 9L3 3M3 3v6M3 3h6M15 9l6-6M21 3h-6M21 3v6M9 15l-6 6M3 21h6M3 21v-6M15 15l6 6M21 21v-6M21 21h-6" stroke="#737373" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <ExpandCanvasIcon />
        )}
      </button>
    </div>

    {/* ── Floating draggable toolbar (fullscreen mode, horizontal) ── */}
    {isFullscreen && <div
      style={{
        position: 'absolute',
        left: fsToolbarPos.x,
        top: fsToolbarPos.y,
        zIndex: 20,
        display: 'flex', flexDirection: 'row', alignItems: 'center',
        border: `1.5px solid ${theme.inputBorder}`,
        borderRadius: 14,
        backgroundColor: theme.cardBg,
        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        userSelect: 'none',
      }}
    >
      {/* Drag handle — left side grip */}
      <div
        onMouseDown={(e) => {
          e.preventDefault();
          isDraggingToolbarRef.current = true;
          const toolbarEl = e.currentTarget.parentElement;
          const rect = toolbarEl.getBoundingClientRect();
          dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        }}
        style={{
          height: '100%', padding: '0 6px',
          cursor: 'grab',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '12px 0 0 12px',
        }}
      >
        <svg width="8" height="18" viewBox="0 0 8 18" fill="none">
          {[2,6].map(cx => [2,7,12,17].map(cy => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.2" fill="#E6E6E6" />
          )))}
        </svg>
      </div>
      {/* Tool buttons */}
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '6px 8px 6px 4px', gap: 2, position: 'relative' }}>
        {TOOLS.map(({ id, Icon, label, isColorPicker, isAction }) => {
          const active = !isAction && !isColorPicker && activeTool === id;
          return (
            <button key={id} title={label}
              onClick={() => {
                if (isColorPicker) colorInputRef.current?.click();
                else if (isAction) { if (id === 'clear') handleClear(); else handleUndo(); }
                else if (id === 'brush') { setShowSizePanel(p => !p); setActiveTool('brush'); }
                else { setActiveTool(id); setShowSizePanel(false); }
              }}
              style={{
                width: 38, height: 38, border: 'none', borderRadius: 10,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: active ? theme.cardBg100 : 'transparent',
                flexShrink: 0, transition: 'background-color 0.12s',
              }}
            >
              {typeof Icon === 'function' && id !== 'color'
                ? <Icon color={active ? theme.toolActiveIcon : theme.text200} size={21} />
                : <Icon />}
            </button>
          );
        })}
        {showSizePanel && (
          <div style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            marginTop: 6, zIndex: 30,
            backgroundColor: theme.cardBg, border: `1.5px solid ${theme.inputBorder}`,
            borderRadius: 12, padding: '8px 10px',
            display: 'flex', flexDirection: 'row', gap: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
          }}>
            {SIZE_OPTIONS.map(sz => (
              <button key={sz} onClick={() => { setStrokeSize(sz); setShowSizePanel(false); }}
                style={{
                  width: 30, height: 30, border: 'none', borderRadius: 8, cursor: 'pointer',
                  backgroundColor: strokeSize === sz ? theme.cardBg100 : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background-color 0.1s',
                }}
              >
                <div style={{ width: sz * 3, height: sz * 3, backgroundColor: theme.text200, borderRadius: '50%' }} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>}

    </div>

    {/* ── Clear canvas confirmation modal (portal → always on top) ── */}
    {showClearModal && createPortal(
      <div
        onClick={closeClearModal}
        style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          backgroundColor: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: isClearClosing ? 'backdropFadeOut 0.8s ease forwards' : 'backdropFadeIn 0.18s ease forwards',
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width: 460, backgroundColor: '#ffffff', borderRadius: 20,
            padding: '28px 28px 24px', display: 'flex', flexDirection: 'column', gap: 24,
            boxShadow: '0 8px 40px rgba(7,20,24,0.20)', zoom: 0.9,
            animation: isClearClosing ? 'modalFadeOut 0.8s ease forwards' : 'modalSpringIn 0.7s ease forwards',
          }}
        >
          <span className="font-satoshi font-bold" style={{ fontSize: 15, color: '#262626', lineHeight: '22px' }}>
            Delete Drawing?
          </span>
          <p className="font-satoshi" style={{ margin: 0, fontSize: 13, color: '#737373', lineHeight: '20px' }}>
            This will clear your entire canvas. This action cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={closeClearModal}
              className="font-satoshi font-bold"
              style={{
                flex: 1, padding: '14px',
                backgroundColor: '#f2f2f2', border: 'none',
                borderRadius: 14, fontSize: 14, color: '#262626', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirmClear}
              className="font-satoshi font-bold"
              style={{
                flex: 1, padding: '14px',
                backgroundColor: '#303f46', border: 'none',
                borderRadius: 14, fontSize: 14, color: '#ffffff', cursor: 'pointer',
              }}
            >
              Proceed
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}
  </div>
  );
}

function DrawAndTellScreen({ onGenerate }) {
  const theme = useContext(ThemeContext);
  const [storyMode,         setStoryMode]         = React.useState('read');
  const [language,          setLanguage]          = React.useState('English (default)');
  const [storyLength,       setStoryLength]       = React.useState('2-5 min (default)');
  const [tags,              setTags]              = React.useState(['5th Birthday', 'Johnny', 'Favourite Gift']);
  const [tagInput,          setTagInput]          = React.useState('');
  const [illustrationStyle, setIllustrationStyle] = React.useState('watercolor');
  const [narrationVoice,    setNarrationVoice]    = React.useState('Smith (Male AI Voice)');
  const [isCanvasFullscreen, setIsCanvasFullscreen] = React.useState(false);

  const showNarrationVoice = storyMode === 'listen' || storyMode === 'watch';

  return (
    <main style={{ flex: 1, overflowY: 'auto', backgroundColor: theme.cardBg100, display: 'flex', flexDirection: 'column' }}>

      {/* Drawing board */}
      <DrawingBoard isFullscreen={isCanvasFullscreen} onToggleFullscreen={() => setIsCanvasFullscreen(f => !f)} />

      {/* Settings panel */}
      <div style={{ backgroundColor: theme.cardBg, margin: '12px', borderRadius: 20, padding: '28px 160px 32px', display: 'flex', flexDirection: 'column', gap: 32, zoom: 0.9 }}>

        {/* Story Mode */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <FieldLabel>Story mode</FieldLabel>
          <div style={{ display: 'flex', gap: 8 }}>
            {STORY_MODES_CONFIG.map(mode => (
              <StoryModeButton key={mode.id} {...mode} active={storyMode === mode.id} onClick={() => setStoryMode(mode.id)} />
            ))}
          </div>
        </div>

        {/* Language + Narration Voice (conditional) + Story Length */}
        <div style={{ display: 'flex', gap: 16, borderTop: '1.5px solid #95acb6', paddingTop: 16, alignItems: 'flex-start', minHeight: 92 }}>
          <SelectField label="Language" value={language} onChange={setLanguage}
            options={['English (default)', 'Spanish', 'French', 'German']} />
          <div style={{
            flex: 'none',
            width: showNarrationVoice ? 'calc((100% - 32px) / 3)' : '0px',
            minWidth: 0, overflow: 'visible',
            opacity: showNarrationVoice ? 1 : 0,
            marginLeft: showNarrationVoice ? 0 : -16,
            transition: showNarrationVoice
              ? 'width 0.5s ease, opacity 0.3s ease, margin-left 0.5s ease'
              : 'width 0.4s ease, opacity 0.3s ease, margin-left 0.4s ease',
          }}>
            <SelectField label="Narration Voice" value={narrationVoice} onChange={setNarrationVoice}
              options={['Smith (Male AI Voice)', 'Emma (Female AI Voice)', 'Aria (Female AI Voice)']} />
          </div>
          <SelectField label="Story Length" value={storyLength} onChange={setStoryLength}
            options={['2-5 min (default)', '5-10 min', '10-15 min']} />
        </div>

        {/* Tagged keywords */}
        <KeywordField
          label="Tagged keywords"
          tags={tags}
          input={tagInput}
          onInputChange={setTagInput}
          onAdd={() => {
            const t = tagInput.trim();
            if (t && !tags.includes(t)) setTags([...tags, t]);
            setTagInput('');
          }}
          onRemove={tag => setTags(tags.filter(t => t !== tag))}
        />

        {/* Illustration Style */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          <div style={{ borderTop: '1.5px solid #E6E6E6', paddingTop: 16 }}>
            <FieldLabel>Illustration style</FieldLabel>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[ILLUSTRATIONS.slice(0, 3), ILLUSTRATIONS.slice(3)].map((row, ri) => (
              <div key={ri} style={{ display: 'flex', gap: 8 }}>
                {row.map(item => (
                  <IllustrationOption key={item.id} {...item} selected={illustrationStyle === item.id} onSelect={setIllustrationStyle} />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Generate Preview */}
        <button
          className="font-satoshi font-bold"
          style={{
            width: '100%', height: 52, backgroundColor: theme.btnPrimaryBg, color: theme.btnPrimaryText,
            border: 'none', borderRadius: 16, fontSize: 14, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'background-color 0.15s, transform 0.1s ease',
          }}
          onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.99)'; }}
          onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          onClick={() => onGenerate(storyMode)}
        >
          <Magicpen size={17} variant="Linear" color={theme.btnPrimaryText} />
          Generate Preview
        </button>

      </div>
    </main>
  );
}

// --- Landing Screen ---

function LandingScreen({ onLogin, onSignup }) {
  const theme = useContext(ThemeContext);
  return (
    <div style={{ flex: 1, alignSelf: 'stretch', display: 'flex', backgroundColor: theme.cardBg }}>

      <style>{`
        @keyframes heartRiseWander1 {
          0%   { transform: translate(0px, 0px) rotate(0deg); opacity: 0; }
          8%   { opacity: 0.35; }
          25%  { transform: translate(-14px, -20vh) rotate(-6deg); }
          50%  { transform: translate(18px,  -48vh) rotate(5deg); }
          75%  { transform: translate(-10px, -72vh) rotate(-8deg); opacity: 0.28; }
          100% { transform: translate(8px,   -115vh) rotate(3deg); opacity: 0; }
        }
        @keyframes heartRiseWander2 {
          0%   { transform: translate(0px, 0px) rotate(0deg); opacity: 0; }
          10%  { opacity: 0.30; }
          30%  { transform: translate(20px,  -22vh) rotate(8deg); }
          55%  { transform: translate(-18px, -50vh) rotate(-5deg); }
          80%  { transform: translate(14px,  -78vh) rotate(7deg); opacity: 0.22; }
          100% { transform: translate(-6px,  -115vh) rotate(-4deg); opacity: 0; }
        }
        @keyframes heartRiseWander3 {
          0%   { transform: translate(0px, 0px) rotate(0deg); opacity: 0; }
          12%  { opacity: 0.38; }
          28%  { transform: translate(-22px, -18vh) rotate(-10deg); }
          52%  { transform: translate(16px,  -46vh) rotate(6deg); }
          76%  { transform: translate(-12px, -74vh) rotate(-7deg); opacity: 0.26; }
          100% { transform: translate(10px,  -115vh) rotate(4deg); opacity: 0; }
        }
      `}</style>

      {/* Left panel — brown with rising hearts + logo */}
      <div style={{ width: '38%', flexShrink: 0, position: 'relative', backgroundColor: '#B7967A', overflow: 'hidden' }}>

        {[
          { size: 70,  left: '6%',  duration: '10.0s', delay: '-2.0s',  anim: 'heartRiseWander1' },
          { size: 44,  left: '38%', duration: '13.0s', delay: '-9.5s',  anim: 'heartRiseWander2' },
          { size: 90,  left: '70%', duration: '9.5s',  delay: '-5.5s',  anim: 'heartRiseWander3' },
          { size: 36,  left: '55%', duration: '11.5s', delay: '-7.0s',  anim: 'heartRiseWander1' },
          { size: 60,  left: '20%', duration: '14.0s', delay: '-11.0s', anim: 'heartRiseWander3' },
          { size: 50,  left: '80%', duration: '10.5s', delay: '-3.5s',  anim: 'heartRiseWander2' },
          { size: 80,  left: '45%', duration: '12.5s', delay: '-6.0s',  anim: 'heartRiseWander1' },
          { size: 42,  left: '88%', duration: '11.0s', delay: '-8.5s',  anim: 'heartRiseWander3' },
          { size: 55,  left: '14%', duration: '13.5s', delay: '-4.5s',  anim: 'heartRiseWander2' },
        ].map((h, i) => (
          <div key={i} style={{
            position: 'absolute', bottom: -80, left: h.left,
            pointerEvents: 'none',
            animation: `${h.anim} ${h.duration} ease-in-out ${h.delay} infinite`,
          }}>
            <Heart size={h.size} variant="Bold" color="#FFFFFF" />
          </div>
        ))}

        {/* Taleo logo centered */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <img src={taleoLogo} alt="Taleo" style={{ width: '72%', objectFit: 'contain' }} />
        </div>
      </div>

      {/* Right panel — welcome + buttons */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 48px' }}>
        <div style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 32, zoom: 0.9 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h1 className="font-bogart" style={{ fontSize: 36, fontWeight: 700, lineHeight: '44px', color: theme.text100, margin: 0 }}>
              Welcome!
            </h1>
            <p className="font-satoshi" style={{ fontSize: 18, lineHeight: '28px', color: theme.text400, margin: 0 }}>
              Today is a new day.<br />It's your day. You shape it.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <button
              onClick={onLogin}
              className="font-satoshi font-bold"
              style={{
                width: '100%', height: 52, borderRadius: 16, border: 'none',
                backgroundColor: theme.btnPrimaryBg, color: theme.btnPrimaryText,
                fontSize: 15, cursor: 'pointer', transition: 'background-color 0.15s',
              }}
            >
              Log in
            </button>
            <button
              onClick={onSignup}
              className="font-satoshi font-bold"
              style={{
                width: '100%', height: 52, borderRadius: 16, border: 'none',
                backgroundColor: theme.cardBg100, color: theme.text200,
                fontSize: 15, cursor: 'pointer', transition: 'background-color 0.15s',
              }}
            >
              Sign up
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}

// --- App ---

export default function App() {
  const [activeNav,        setActiveNav]        = useState('home');
  const [activeProfile,    setActiveProfile]    = useState({ name: 'Kiraz', src: AVATAR_URL });
  const [darkMode,         setDarkMode]         = useState(false);
  const [showCreateModal,  setShowCreateModal]  = useState(false);
  const [isModalClosing,   setIsModalClosing]   = useState(false);
  const [storyType,        setStoryType]        = useState('personalised');
  const [currentScreen,    setCurrentScreen]    = useState('landing'); // 'landing' | 'login' | 'select-profile' | 'signup' | 'preferences' | 'child-profile' | 'home' | 'draw-tell' | 'dice' | 'personalised' | 'freeform' | 'step2' | 'preview' | 'story-read' | 'listen-audio' | 'watch-video'
  const [selectedMode,     setSelectedMode]     = useState('read');
  const [previewOrigin,    setPreviewOrigin]    = useState('step2'); // which screen the preview's back button returns to
  const [childProfileSource, setChildProfileSource] = useState('onboarding'); // 'onboarding' | 'manage-profile'
  const [showModifyModal,  setShowModifyModal]  = useState(false);
  const [isModifyClosing,  setIsModifyClosing]  = useState(false);
  const [profileMenuOpen,        setProfileMenuOpen]        = useState(false);
  const [isProfileMenuClosing,   setIsProfileMenuClosing]   = useState(false);
  const [shareThoughtsOpen,      setShareThoughtsOpen]      = useState(false);
  const [isShareThoughtsClosing, setIsShareThoughtsClosing] = useState(false);
  const [storyPreviewOpen,       setStoryPreviewOpen]       = useState(false);
  const [isStoryPreviewClosing,  setIsStoryPreviewClosing]  = useState(false);
  const [previewStory,           setPreviewStory]           = useState(null);

  const goHome = () => { setCurrentScreen('home'); setActiveNav('home'); };

  const closeProfileMenu = () => {
    setIsProfileMenuClosing(true);
    setTimeout(() => { setProfileMenuOpen(false); setIsProfileMenuClosing(false); }, 500);
  };

  const closeShareThoughts = () => {
    setIsShareThoughtsClosing(true);
    setTimeout(() => { setShareThoughtsOpen(false); setIsShareThoughtsClosing(false); }, 300);
  };

  const closeStoryPreview = () => {
    setIsStoryPreviewClosing(true);
    setTimeout(() => { setStoryPreviewOpen(false); setIsStoryPreviewClosing(false); }, 300);
  };

  const closeModal = (afterClose) => {
    setIsModalClosing(true);
    setTimeout(() => {
      setShowCreateModal(false);
      setIsModalClosing(false);
      afterClose?.();
    }, 760);
  };

  const openModify  = () => { setShowModifyModal(true); setIsModifyClosing(false); };
  const closeModify = () => {
    setIsModifyClosing(true);
    setTimeout(() => { setShowModifyModal(false); setIsModifyClosing(false); }, 760);
  };

  const theme = getTheme(darkMode);

  return (
    <ThemeContext.Provider value={theme}>
    {/* Full viewport — gray background */}
    <div
      style={{
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: '#333333',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <GlobalStyles />

      {/* Tablet-sized app container */}
      <div
        data-app-root
        style={{
          position: 'relative',
          width: CONTAINER_W,
          height: '90vh',
          maxHeight: 700,
          borderRadius: 16,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        }}
      >
        {currentScreen === 'landing' && (
          <LandingScreen
            onLogin={() => setCurrentScreen('login')}
            onSignup={() => setCurrentScreen('signup')}
          />
        )}
        {currentScreen === 'login' && (
          <LoginScreen
            onLogin={() => setCurrentScreen('select-profile')}
            onSignup={() => setCurrentScreen('signup')}
          />
        )}
        {currentScreen === 'select-profile' && (
          <SelectProfileScreen
            onBack={() => setCurrentScreen('login')}
            onNext={(id) => {
              if (id === 'eda')   setActiveProfile({ name: 'Eda',   src: EDA_URL });
              else                setActiveProfile({ name: 'Kiraz', src: AVATAR_URL });
              setCurrentScreen('home');
            }}
          />
        )}
        {currentScreen === 'signup' && (
          <SignupScreen
            onLogin={() => setCurrentScreen('login')}
            onNext={() => setCurrentScreen('preferences')}
          />
        )}
        {currentScreen === 'preferences' && (
          <PreferencesScreen
            onSkip={() => setCurrentScreen('home')}
            onNext={() => { setChildProfileSource('onboarding'); setCurrentScreen('child-profile'); }}
          />
        )}
        {currentScreen === 'child-profile' && (
          <ChildProfileScreen
            onSkip={() => setCurrentScreen(childProfileSource === 'manage-profile' ? 'manage-profile' : 'home')}
            onCreate={() => setCurrentScreen(childProfileSource === 'manage-profile' ? 'manage-profile' : 'home')}
          />
        )}
        {currentScreen !== 'landing' && showModifyModal && (
          <ModifyPromptModal onClose={closeModify} isClosing={isModifyClosing} />
        )}

        {/* Share Thoughts modal */}
        {shareThoughtsOpen && (
          <ShareThoughtsModal
            onClose={closeShareThoughts}
            isClosing={isShareThoughtsClosing}
            avatarUrl={EDA_URL}
          />
        )}

        {/* Story Preview modal */}
        {storyPreviewOpen && previewStory && (
          <StoryPreviewModal
            story={previewStory}
            onClose={closeStoryPreview}
            isClosing={isStoryPreviewClosing}
            onModeSelect={(mode) => {
              closeStoryPreview();
              setSelectedMode(mode);
              setPreviewOrigin('library');
              if (mode === 'listen') setCurrentScreen('listen-audio');
              else if (mode === 'watch') setCurrentScreen('watch-video');
              else setCurrentScreen('story-read');
            }}
          />
        )}

        {/* Profile menu overlay + dropdown */}
        {profileMenuOpen && (
          <>
            {/* Dark blurred overlay */}
            <div
              style={{
                position: 'absolute', inset: 0, zIndex: 200,
                backgroundColor: 'rgba(0,0,0,0.5)',
              }}
              onClick={closeProfileMenu}
            />
            {/* Dropdown */}
            <div
              style={{
                position: 'absolute',
                top: (HEADER_H - 46) / 2 + 46 + 8,
                right: 24,
                zIndex: 201,
                transformOrigin: 'top right',
                animation: isProfileMenuClosing
                  ? 'profile-menu-out 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards'
                  : 'profile-menu-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards',
                backgroundColor: theme.cardBg,
                borderRadius: 9,
                width: 180,
                paddingTop: 9,
                paddingBottom: 9,
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              }}
              onClick={e => e.stopPropagation()}
            >
              {[
                { label: 'Notifications', badge: 5, action: 'notifications' },
                { label: 'Statistics', action: 'statistics' },
                { label: 'Manage Profile', action: 'manage-profile' },
                { label: 'Log out', action: 'logout' },
              ].map(({ label, badge, action }) => (
                <div
                  key={label}
                  onClick={() => {
                    closeProfileMenu();
                    setTimeout(() => {
                      if (action === 'logout') setCurrentScreen('login');
                      if (action === 'notifications') setCurrentScreen('notifications');
                      if (action === 'statistics') setCurrentScreen('statistics');
                      if (action === 'manage-profile') setCurrentScreen('manage-profile');
                    }, 500);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9,
                    padding: '6px 18px',
                    cursor: 'pointer',
                    transition: 'background-color 0.1s',
                  }}
                >
                  <span
                    className="font-satoshi"
                    style={{ flex: 1, fontSize: 14, lineHeight: '21px', color: theme.text100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {label}
                  </span>
                  {badge && (
                    <div
                      style={{
                        width: 18, height: 18, borderRadius: '50%',
                        backgroundColor: '#D46C6C',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <span className="font-satoshi font-bold" style={{ fontSize: 9, color: '#FFFFFF', lineHeight: 1 }}>
                        {badge}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        {currentScreen !== 'landing' && showCreateModal && (
          <CreateStoryModal
            storyType={storyType}
            onSelectType={setStoryType}
            isClosing={isModalClosing}
            onClose={() => closeModal()}
            onNext={() => closeModal(() => {
              if (storyType === 'personalised') setCurrentScreen('personalised');
              else if (storyType === 'freeform') setCurrentScreen('freeform');
            })}
          />
        )}
        {/* Sidebar runs full height, content column on the right */}
        {currentScreen !== 'landing' && currentScreen !== 'login' && currentScreen !== 'select-profile' && currentScreen !== 'signup' && currentScreen !== 'preferences' && currentScreen !== 'child-profile' && <Sidebar
          activeNav={currentScreen === 'home' ? 'home' : currentScreen === 'settings' ? 'menu' : (activeNav === 'home' ? null : activeNav)}
          onNavChange={(id) => {
            if (id === 'menu') { setCurrentScreen('settings'); }
            if (id === 'home') { setCurrentScreen('home'); }
            if (id === 'dice') { setCurrentScreen('dice'); }
            if (id === 'community') { setCurrentScreen('community'); }
            if (id === 'library') { setCurrentScreen('library'); }
            setActiveNav(id);
          }}
          darkMode={darkMode}
          onToggleDark={() => setDarkMode(d => !d)}
        />}

        {currentScreen !== 'landing' && currentScreen !== 'login' && currentScreen !== 'select-profile' && currentScreen !== 'signup' && currentScreen !== 'preferences' && currentScreen !== 'child-profile' && <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {currentScreen === 'library' ? (
            <>
              <Header title="Library" subtitle="Pick a story, spark a moment! These tales are ready when you are." onAvatarClick={() => setProfileMenuOpen(true)} mascotUrl={LIBRARY_MASCOT_URL} avatarUrl={activeProfile.src} />
              <LibraryScreen onStoryClick={(story) => { setPreviewStory(story); setStoryPreviewOpen(true); }} />
            </>
          ) : currentScreen === 'community' ? (
            <>
              <Header title="Community & Blogs" subtitle="Because you shouldn't have to figure it all out alone." onAvatarClick={() => setProfileMenuOpen(true)} mascotUrl={COMMUNITY_MASCOT_URL} avatarUrl={activeProfile.src} />
              <CommunityScreen onShareThoughts={() => setShareThoughtsOpen(true)} />
            </>
          ) : currentScreen === 'notifications' ? (
            <>
              <Header title="Notifications" subtitle="Your latest updates" onAvatarClick={() => setProfileMenuOpen(true)} avatarUrl={activeProfile.src} />
              <NotificationsScreen />
            </>
          ) : currentScreen === 'settings' ? (
            <>
              <Header title="Settings" subtitle="Manage all your settings" onAvatarClick={() => setProfileMenuOpen(true)} mascotUrl={SETTINGS_DOGGO_URL} mascotScale={1.3} mascotOffsetY={-10} avatarUrl={activeProfile.src} />
              <SettingsScreen />
            </>
          ) : currentScreen === 'statistics' ? (
            <>
              <Header title="Profile" subtitle="Profile Statistics" onAvatarClick={() => setProfileMenuOpen(true)} avatarUrl={activeProfile.src} />
              <StatisticsScreen onTabChange={(tab) => setCurrentScreen(tab)} />
            </>
          ) : currentScreen === 'manage-profile' ? (
            <>
              <Header title="Profile" subtitle="Manage Profile" onAvatarClick={() => setProfileMenuOpen(true)} avatarUrl={activeProfile.src} />
              <ManageProfileScreen
                onTabChange={(tab) => setCurrentScreen(tab)}
                onEditProfile={(id) => {
                  if (id === 'new') { setChildProfileSource('manage-profile'); setCurrentScreen('child-profile'); }
                  else setCurrentScreen(id === 'kiraz' ? 'edit-kid-profile' : 'edit-profile');
                }}
              />
            </>
          ) : currentScreen === 'edit-profile' ? (
            <>
              <Header title="Profile" subtitle="Profile Statistics" onAvatarClick={() => setProfileMenuOpen(true)} avatarUrl={activeProfile.src} />
              <EditProfileScreen onTabChange={(tab) => setCurrentScreen(tab)} onSave={() => setCurrentScreen('manage-profile')} />
            </>
          ) : currentScreen === 'edit-kid-profile' ? (
            <>
              <Header title="Profile" subtitle="Profile Statistics" onAvatarClick={() => setProfileMenuOpen(true)} avatarUrl={activeProfile.src} />
              <EditKidProfileScreen onTabChange={(tab) => setCurrentScreen(tab)} onSave={() => setCurrentScreen('manage-profile')} />
            </>
          ) : currentScreen === 'home' ? (
            <>
              <Header title={`Welcome ${activeProfile.name}!`} onAvatarClick={() => setProfileMenuOpen(true)} avatarUrl={activeProfile.src} />
              <MainContent
                onCreateClick={() => setShowCreateModal(true)}
                onDrawClick={() => setCurrentScreen('draw-tell')}
              />
            </>
          ) : currentScreen === 'draw-tell' ? (
            <>
              <Header
                title="Draw & Tell Mode"
                subtitle="Draw your story, we'll bring it to life!"
                onAvatarClick={() => setProfileMenuOpen(true)}
                mascotUrl={DRAW_TELL_MASCOT_URL}
                avatarUrl={activeProfile.src}
              />
              <DrawAndTellScreen
                onGenerate={(mode) => { setSelectedMode(mode); setPreviewOrigin('draw-tell'); setCurrentScreen('preview'); }}
              />
            </>
          ) : currentScreen === 'dice' ? (
            <>
              <Header
                title="Story Dice"
                subtitle="Every day holds a new story! Let's create one together."
                onAvatarClick={() => setProfileMenuOpen(true)}
                mascotUrl={DICE_MASCOT_URL}
                avatarUrl={activeProfile.src}
              />
              <StoryDiceScreen onBack={goHome} />
            </>
          ) : currentScreen === 'personalised' ? (
            <>
              <Header
                title="Personalised Story Mode"
                subtitle="Step 1/2 — Choose Your Preferences"
                mascotUrl={MODE_MASCOT_URL}
                avatarUrl={activeProfile.src}
              />
              <PersonalisedStoryScreen
                onBack={goHome}
                onNext={(mode) => { setSelectedMode(mode); setCurrentScreen('step2'); }}
              />
            </>
          ) : currentScreen === 'freeform' ? (
            <>
              <Header
                title="Free Form Story Mode"
                subtitle="Let your imagination lead, we'll shape it into a story."
                mascotUrl={MODE_MASCOT_URL}
                avatarUrl={activeProfile.src}
              />
              <FreeformStoryScreen
                onBack={goHome}
                onGenerate={(mode) => { setSelectedMode(mode); setPreviewOrigin('freeform'); setCurrentScreen('preview'); }}
              />
            </>
          ) : currentScreen === 'step2' ? (
            <>
              <Header
                title="Personalised Story Mode"
                subtitle="Step 2/2 — Add Your Personalised Details"
              />
              <PersonalisedStoryStep2Screen
                onBack={() => setCurrentScreen('personalised')}
                onGenerate={() => { setPreviewOrigin('step2'); setCurrentScreen('preview'); }}
              />
            </>
          ) : currentScreen === 'preview' ? (
            <StoryPreviewScreen
              onBack={() => setCurrentScreen(previewOrigin)}
              onModify={openModify}
              onPublish={() => {
                if (selectedMode === 'listen') setCurrentScreen('listen-audio');
                else if (selectedMode === 'watch') setCurrentScreen('watch-video');
                else setCurrentScreen('story-read');
              }}
              previewOrigin={previewOrigin}
            />
          ) : currentScreen === 'listen-audio' ? (
            <>
              <Header
                title={previewOrigin === 'library' ? 'Library' : previewOrigin === 'freeform' ? 'Free Form Story Mode' : previewOrigin === 'draw-tell' ? 'Draw & Tell Mode' : 'Personalised Story Mode'}
                subtitle={previewOrigin === 'library' ? 'Pick a story, spark a moment! These tales are ready when you are.' : previewOrigin === 'draw-tell' ? "Every day holds a new story! Let's create one together." : "Let your imagination lead, we'll shape it into a story."}
                mascotUrl={previewOrigin === 'library' ? HAMMY_URL : undefined}
              />
              <ListenModeAudioScreen
                onBack={() => setCurrentScreen(previewOrigin === 'library' ? 'library' : 'preview')}
                story={previewOrigin === 'library' ? previewStory : previewOrigin === 'draw-tell' ? null : { title: 'Ryan the Forest Explorer', image: STORY_READ_HERO_URL }}
                previewOrigin={previewOrigin}
              />
            </>
          ) : currentScreen === 'watch-video' ? (
            <WatchModeScreen
              onBack={() => setCurrentScreen(previewOrigin === 'library' ? 'library' : 'preview')}
              story={previewOrigin === 'library' ? previewStory : previewOrigin === 'draw-tell' ? null : { title: 'Ryan the Forest Explorer', image: STORY_READ_HERO_URL }}
              previewOrigin={previewOrigin}
            />
          ) : (
            <StoryReadScreen
              onBack={() => setCurrentScreen(previewOrigin === 'library' ? 'library' : 'preview')}
              previewOrigin={previewOrigin}
              story={previewOrigin === 'library' ? previewStory : previewOrigin === 'draw-tell' ? null : { title: 'Ryan the forest Explorer', category: 'User-Generated Stories', ageGroup: '3 to 5 year old', image: STORY_READ_HERO_URL }}
              onListen={() => setCurrentScreen('listen-audio')}
              onWatch={() => setCurrentScreen('watch-video')}
            />
          )}
        </div>}
      </div>
    </div>
    </ThemeContext.Provider>
  );
}
